/**
 * Enhanced Wealth Gap Details Modal
 * Comprehensive breakdown with assumptions, projections, and charts
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Target, X, ChevronDown, ChevronUp, TrendingUp, DollarSign, Calendar, Download } from 'lucide-react';
import { formatCurrency } from '../../utils/enhancedDashboardUtils';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { useBusinessContext } from '../../contexts/BusinessContext';

const WealthGapDetailsModalEnhanced = ({ show, onClose, wealthGap }) => {
  const { currentBusinessId } = useBusinessContext();
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const handleDownloadPDF = async () => {
    if (!currentBusinessId) {
      alert('No business selected');
      return;
    }

    setDownloadingPDF(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/pdf/wealth-gap-details/${currentBusinessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-Id': currentBusinessId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : 'Wealth_Gap_Details.pdf';

      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };
  const [expandedSections, setExpandedSections] = useState({
    assumptions: true,
    saleProceeds: false,
    dataFlow: false,
    spending: false,
    investment: false,
    scenarios: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!show || !wealthGap) return null;

  // Get required sale price data
  const requiredData = wealthGap.required_sale_price_data || {};
  const hasRequiredData = wealthGap.wealth_goal_method === 'monthly_needs' && requiredData.total_capital_needed !== undefined;

  // Calculate key metrics
  const currentYear = new Date().getFullYear();
  const yearsUntilExit = Math.max(0, (wealthGap.exit_year || currentYear + 2) - currentYear);
  const ageAtExit = wealthGap.current_age + yearsUntilExit;
  const yearsUntilRetirement = Math.max(0, wealthGap.retirement_age - wealthGap.current_age);

  // Calculate outside income growth
  const outsideIncomeGrowthRate = (wealthGap.outside_income_annual_increase || 0) / 100;
  const futureOutsideIncomeMonthly = (wealthGap.outside_income_monthly || 0) * Math.pow(1 + outsideIncomeGrowthRate, yearsUntilRetirement);

  // Calculate monthly need at exit with inflation
  // monthly_cash_need is now the TOTAL (allocation breakdown is tracked separately)
  const totalCurrentMonthlyNeed = wealthGap.monthly_cash_need || 0;
  const inflationMultiplier = Math.pow(1 + (wealthGap.annual_inflation_rate || 2) / 100, yearsUntilExit);
  const monthlyNeedAtExit = totalCurrentMonthlyNeed * inflationMultiplier * (1 + (wealthGap.lifestyle_change_percentage || 0) / 100);

  // Current and future net monthly needs
  const currentNetMonthlyNeed = Math.max(0, totalCurrentMonthlyNeed - (wealthGap.outside_income_monthly || 0));
  const netMonthlyNeed = Math.max(0, monthlyNeedAtExit - futureOutsideIncomeMonthly);

  // Ownership and tax rates
  const ownershipDecimal = (wealthGap.ownership_percentage || 100) / 100;
  const taxDecimal = (wealthGap.tax_rate_on_sale || 42) / 100;

  // Calculate after-tax proceeds from future business value (if provided)
  const futureBusinessValueDisplay = wealthGap.future_business_value || wealthGap.desired_sale_price || 0;
  const desiredAfterTaxProceeds = futureBusinessValueDisplay
    ? futureBusinessValueDisplay * ownershipDecimal * (1 - taxDecimal)
    : 0;

  // Helper function to calculate FV with monthly withdrawals (growing annuity)
  const calculateFVwithPMT = (pv, annualRate, years, monthlyPmt, annualGrowthRate = 0) => {
    if (years === 0) return pv;
    const monthlyRate = annualRate / 12 / 100;
    const monthlyGrowthRate = annualGrowthRate / 12 / 100;
    const n = years * 12; // Total months

    // Calculate FV of the present value
    const fvPV = pv * Math.pow(1 + monthlyRate, n);

    // Calculate FV of growing withdrawals
    if (monthlyPmt === 0) {
      return fvPV;
    }

    // Handle edge case where return rate equals growth rate
    if (Math.abs(monthlyRate - monthlyGrowthRate) < 1e-10) {
      // When r = g: FV = PV(1+r)^n - PMT * n * (1+r)^(n-1)
      const fvPMT = monthlyPmt * n * Math.pow(1 + monthlyRate, n - 1);
      return fvPV - fvPMT;
    }

    // Normal case: Growing annuity formula
    const fvPMT = monthlyPmt * ((Math.pow(1 + monthlyRate, n) - Math.pow(1 + monthlyGrowthRate, n)) / (monthlyRate - monthlyGrowthRate));
    return fvPV - fvPMT;
  };

  // Calculate monthly withdrawal from the selected source
  const monthlyWithdrawal = wealthGap.monthly_cash_need_from_non_business || 0;
  const nonBusinessSourceCategory = wealthGap.non_business_source_category || 'liquid_assets';
  const annualInflationRate = wealthGap.annual_inflation_rate || 2;

  // Business sale proceeds calculations
  const lumpSumPercent = wealthGap.lump_sum_percent || 0;
  const lumpSumDollar = wealthGap.lump_sum_dollar || 0;
  const ownershipPct = (wealthGap.ownership_percentage || 100) / 100;
  const taxRate = wealthGap.tax_rate_on_sale !== undefined ? wealthGap.tax_rate_on_sale / 100 : 0.42;
  const lumpSumTaxRate = taxRate; // Alias for clarity in displays
  const grossProceeds = futureBusinessValueDisplay * ownershipPct;
  const lumpSumGross = lumpSumDollar > 0 ? lumpSumDollar : (grossProceeds * lumpSumPercent / 100);
  const lumpSumAmount = lumpSumGross * (1 - taxRate);

  // Lump sum allocation percentages
  const lumpSumAllocLiquid = wealthGap.lump_sum_allocation_liquid_assets || 0;
  const lumpSumAllocRealEstate = wealthGap.lump_sum_allocation_real_estate || 0;
  const lumpSumAllocOtherInv = wealthGap.lump_sum_allocation_other_investments !== undefined
    ? wealthGap.lump_sum_allocation_other_investments
    : 100;

  // Lump sum amounts by asset category
  const lumpSumToLiquid = lumpSumAmount * (lumpSumAllocLiquid / 100);
  const lumpSumToRealEstate = lumpSumAmount * (lumpSumAllocRealEstate / 100);
  const lumpSumToOtherInv = lumpSumAmount * (lumpSumAllocOtherInv / 100);

  // Calculate FV for each asset class (with growing annuity if withdrawal source)
  const liquidAssetsRate = wealthGap.liquid_assets_return_rate !== undefined ? wealthGap.liquid_assets_return_rate : 3;
  const liquidAssetsFVBase = nonBusinessSourceCategory === 'liquid_assets'
    ? calculateFVwithPMT(wealthGap.liquid_assets || 0, liquidAssetsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap.liquid_assets || 0) * Math.pow(1 + liquidAssetsRate / 100, yearsUntilExit);

  const retirementAccountsRate = wealthGap.retirement_accounts_return_rate !== undefined ? wealthGap.retirement_accounts_return_rate : 7;
  const retirementAccountsFVBase = nonBusinessSourceCategory === 'retirement_accounts'
    ? calculateFVwithPMT(wealthGap.retirement_accounts || 0, retirementAccountsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap.retirement_accounts || 0) * Math.pow(1 + retirementAccountsRate / 100, yearsUntilExit);

  const realEstateRate = wealthGap.real_estate_return_rate !== undefined ? wealthGap.real_estate_return_rate : 4;
  const realEstateFVBase = nonBusinessSourceCategory === 'real_estate'
    ? calculateFVwithPMT(wealthGap.real_estate_equity || 0, realEstateRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap.real_estate_equity || 0) * Math.pow(1 + realEstateRate / 100, yearsUntilExit);

  const otherInvestmentsRate = wealthGap.other_investments_return_rate !== undefined ? wealthGap.other_investments_return_rate : 6;
  const otherInvestmentsFVBase = nonBusinessSourceCategory === 'other_investments'
    ? calculateFVwithPMT(wealthGap.other_investments || 0, otherInvestmentsRate, yearsUntilExit, monthlyWithdrawal, annualInflationRate)
    : (wealthGap.other_investments || 0) * Math.pow(1 + otherInvestmentsRate / 100, yearsUntilExit);

  // Future value at exit (personal assets only, no business proceeds)
  const liquidAssetsFV = liquidAssetsFVBase;
  const retirementAccountsFV = retirementAccountsFVBase;
  const realEstateFV = realEstateFVBase;
  const otherInvestmentsFV = otherInvestmentsFVBase;

  // For backward compatibility in UI display
  const otherInvestmentsBaseFV = otherInvestmentsFVBase;
  const otherInvestmentsFVWithLumpSum = otherInvestmentsFVBase + lumpSumToOtherInv; // Legacy display

  // Calculate future liabilities
  const futureLiabilities = (wealthGap.total_liabilities || 0) * Math.pow(1 - (wealthGap.liability_reduction_rate || 0) / 100, yearsUntilExit);

  // Calculate earnout
  const earnoutGross = grossProceeds - lumpSumGross;
  const earnoutTaxRate = wealthGap.earnout_tax_rate !== undefined ? wealthGap.earnout_tax_rate / 100 : 0.42;
  const earnoutYears = wealthGap.earnout_years || 5;
  const earnoutInterestRate = wealthGap.earnout_interest_rate || 5;
  const earnoutPaymentFrequency = wealthGap.earnout_payment_frequency || 'annual';

  // Calculate earnout payment (annuity formula)
  const calculateAnnuityPayment = (pv, rate, years, frequency) => {
    if (pv === 0 || years === 0) return 0;
    const r = rate / 100;
    const n = years;
    if (r === 0) return pv / n;
    const annualPayment = pv * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return frequency === 'monthly' ? annualPayment / 12 : annualPayment;
  };

  const earnoutPaymentGross = calculateAnnuityPayment(
    earnoutGross,
    earnoutInterestRate,
    earnoutYears,
    earnoutPaymentFrequency
  );

  // Apply tax to earnout payment
  const earnoutPaymentAfterTax = earnoutPaymentGross * (1 - earnoutTaxRate);

  // Earnout allocation percentages
  const earnoutAllocLiquid = wealthGap.earnout_allocation_liquid_assets || 0;
  const earnoutAllocRealEstate = wealthGap.earnout_allocation_real_estate || 0;
  const earnoutAllocOtherInv = wealthGap.earnout_allocation_other_investments !== undefined
    ? wealthGap.earnout_allocation_other_investments
    : 100;

  // Earnout payment amounts by asset category (per payment)
  const earnoutPaymentToLiquid = earnoutPaymentAfterTax * (earnoutAllocLiquid / 100);
  const earnoutPaymentToRealEstate = earnoutPaymentAfterTax * (earnoutAllocRealEstate / 100);
  const earnoutPaymentToOtherInv = earnoutPaymentAfterTax * (earnoutAllocOtherInv / 100);

  // Monthly earnout payments by asset category (for FV calculations)
  const monthlyEarnoutPaymentLiquid = earnoutPaymentFrequency === 'monthly'
    ? earnoutPaymentToLiquid
    : earnoutPaymentToLiquid / 12;
  const monthlyEarnoutPaymentRealEstate = earnoutPaymentFrequency === 'monthly'
    ? earnoutPaymentToRealEstate
    : earnoutPaymentToRealEstate / 12;
  const monthlyEarnoutPaymentOtherInv = earnoutPaymentFrequency === 'monthly'
    ? earnoutPaymentToOtherInv
    : earnoutPaymentToOtherInv / 12;

  // For backward compatibility in UI display
  const monthlyEarnoutPayment = earnoutPaymentFrequency === 'monthly'
    ? earnoutPaymentAfterTax
    : earnoutPaymentAfterTax / 12;

  // Total future net worth at exit (personal assets with lump sum but before earnout)
  const futureNetWorth = liquidAssetsFV + retirementAccountsFV + realEstateFV + otherInvestmentsFV - futureLiabilities;

  // Calculate wealth gap/surplus using locally calculated futureNetWorth
  const totalCapitalNeeded = requiredData.total_capital_needed || 0;
  const wealthGapAmount = totalCapitalNeeded - futureNetWorth;
  const localWealthSurplus = Math.max(0, -wealthGapAmount); // Positive if surplus
  const localNetGapFromBusiness = Math.max(0, wealthGapAmount); // Positive if gap

  // Calculate required sale price from local net gap
  // Required Sale Price = After-Tax Proceeds Needed / (Ownership% * (1 - Tax%))
  const localRequiredSalePrice = localNetGapFromBusiness > 0
    ? localNetGapFromBusiness / (ownershipDecimal * (1 - taxDecimal))
    : 0;

  // Calculate after-tax proceeds from locally calculated required sale price
  const afterTaxProceeds = localRequiredSalePrice * ownershipDecimal * (1 - taxDecimal);

  // POST-EXIT CALCULATIONS (from exit to life expectancy)
  const lifeExp = parseInt(wealthGap.life_expectancy) || 100;
  const yearsPostExit = Math.max(0, lifeExp - wealthGap.retirement_age);
  const postExitMonthlyWithdrawal = netMonthlyNeed; // Net monthly need at exit (already calculated)
  const postExitSourceCategory = wealthGap.post_exit_source_category || 'liquid_assets';

  // Post-exit return rates
  const postExitLiquidAssetsRate = wealthGap.post_exit_liquid_assets_return_rate !== undefined ? wealthGap.post_exit_liquid_assets_return_rate : 3;
  const postExitRetirementAccountsRate = wealthGap.post_exit_retirement_accounts_return_rate !== undefined ? wealthGap.post_exit_retirement_accounts_return_rate : 7;
  const postExitRealEstateRate = wealthGap.post_exit_real_estate_return_rate !== undefined ? wealthGap.post_exit_real_estate_return_rate : 4;
  const postExitOtherInvestmentsRate = wealthGap.post_exit_other_investments_return_rate !== undefined ? wealthGap.post_exit_other_investments_return_rate : 6;
  const postExitLiabilityRate = wealthGap.post_exit_liability_reduction_rate !== undefined ? wealthGap.post_exit_liability_reduction_rate : 0;

  // Calculate FV at life expectancy for each asset class with earnout allocations
  const earnoutPeriodYears = earnoutYears;
  const yearsWithEarnout = Math.min(yearsPostExit, earnoutPeriodYears);
  const yearsAfterEarnout = Math.max(0, yearsPostExit - earnoutPeriodYears);

  // Liquid Assets: Add lump sum at exit, then during earnout period add earnout payments; after, just withdrawals
  const liquidPhase1Withdrawal = postExitSourceCategory === 'liquid_assets' ? postExitMonthlyWithdrawal : 0;
  const liquidNetPMTDuringEarnout = monthlyEarnoutPaymentLiquid - liquidPhase1Withdrawal;
  const liquidAssetsFVAfterEarnout = calculateFVwithPMT(
    liquidAssetsFV + lumpSumToLiquid, // Add lump sum at exit
    postExitLiquidAssetsRate,
    yearsWithEarnout,
    -liquidNetPMTDuringEarnout, // Negative because FV formula treats PMT as outflow
    annualInflationRate
  );
  const liquidPhase2Withdrawal = postExitSourceCategory === 'liquid_assets' ? postExitMonthlyWithdrawal : 0;
  const liquidAssetsFVEndOfLife = yearsAfterEarnout > 0
    ? calculateFVwithPMT(liquidAssetsFVAfterEarnout, postExitLiquidAssetsRate, yearsAfterEarnout, liquidPhase2Withdrawal, annualInflationRate)
    : liquidAssetsFVAfterEarnout;

  // Retirement Accounts: No earnout, only withdrawals
  const retirementAccountsFVEndOfLife = postExitSourceCategory === 'retirement_accounts'
    ? calculateFVwithPMT(retirementAccountsFV, postExitRetirementAccountsRate, yearsPostExit, postExitMonthlyWithdrawal, annualInflationRate)
    : retirementAccountsFV * Math.pow(1 + postExitRetirementAccountsRate / 100, yearsPostExit);

  // Real Estate: Add lump sum at exit, then during earnout period add earnout payments; after, just withdrawals
  const realEstatePhase1Withdrawal = (postExitSourceCategory === 'real_estate' || postExitSourceCategory === 'real_estate_equity') ? postExitMonthlyWithdrawal : 0;
  const realEstateNetPMTDuringEarnout = monthlyEarnoutPaymentRealEstate - realEstatePhase1Withdrawal;
  const realEstateFVAfterEarnout = calculateFVwithPMT(
    realEstateFV + lumpSumToRealEstate, // Add lump sum at exit
    postExitRealEstateRate,
    yearsWithEarnout,
    -realEstateNetPMTDuringEarnout,
    annualInflationRate
  );
  const realEstatePhase2Withdrawal = (postExitSourceCategory === 'real_estate' || postExitSourceCategory === 'real_estate_equity') ? postExitMonthlyWithdrawal : 0;
  const realEstateFVEndOfLife = yearsAfterEarnout > 0
    ? calculateFVwithPMT(realEstateFVAfterEarnout, postExitRealEstateRate, yearsAfterEarnout, realEstatePhase2Withdrawal, annualInflationRate)
    : realEstateFVAfterEarnout;

  // Other Investments: Add lump sum at exit, then during earnout period add earnout payments; after, just withdrawals
  const otherInvPhase1Withdrawal = postExitSourceCategory === 'other_investments' ? postExitMonthlyWithdrawal : 0;
  const otherInvNetPMTDuringEarnout = monthlyEarnoutPaymentOtherInv - otherInvPhase1Withdrawal;
  const otherInvestmentsFVAfterEarnout = calculateFVwithPMT(
    otherInvestmentsFV + lumpSumToOtherInv, // Add lump sum at exit
    postExitOtherInvestmentsRate,
    yearsWithEarnout,
    -otherInvNetPMTDuringEarnout,
    annualInflationRate
  );
  const otherInvPhase2Withdrawal = postExitSourceCategory === 'other_investments' ? postExitMonthlyWithdrawal : 0;
  const otherInvestmentsFVEndOfLife = yearsAfterEarnout > 0
    ? calculateFVwithPMT(otherInvestmentsFVAfterEarnout, postExitOtherInvestmentsRate, yearsAfterEarnout, otherInvPhase2Withdrawal, annualInflationRate)
    : otherInvestmentsFVAfterEarnout;

  const liabilitiesFVEndOfLife = futureLiabilities * Math.pow(1 - postExitLiabilityRate / 100, yearsPostExit);

  // Total net worth at end of life
  const netWorthAtEndOfLife = liquidAssetsFVEndOfLife + retirementAccountsFVEndOfLife + realEstateFVEndOfLife + otherInvestmentsFVEndOfLife - liabilitiesFVEndOfLife;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-full sm:max-w-3xl md:max-w-4xl lg:max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header - Responsive */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-red-600 p-4 sm:p-6 flex justify-between items-center z-10">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <Target size={24} className="text-white flex-shrink-0 sm:w-7 sm:h-7" />
            <div className="min-w-0">
              <h3 className="text-lg sm:text-2xl font-bold text-white truncate">Wealth Gap Analysis</h3>
              <p className="text-orange-100 text-xs sm:text-sm hidden sm:block">Comprehensive Financial Exit Planning</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}
              className="text-white/90 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-lg p-2 flex items-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px] min-h-[44px]"
              title="Download PDF"
            >
              <Download className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">
                {downloadingPDF ? 'Generating...' : 'PDF'}
              </span>
            </button>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-lg p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content - Responsive padding */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Summary Card */}
          <div className={`border-2 rounded-xl p-6 ${hasRequiredData && localWealthSurplus > 0 ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200'}`}>
            <h4 className="font-bold text-2xl text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className={hasRequiredData && localWealthSurplus > 0 ? 'text-green-600' : 'text-orange-600'} size={28} />
              Wealth Gap Analysis
            </h4>

            {wealthGap.wealth_goal_method === 'single_number' ? (
              /* Single Number Method Display */
              <div className="space-y-4">
                {/* Wealth Goal */}
                <div className="bg-white rounded-lg p-6 border-2 border-blue-300">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700 font-semibold text-lg">Wealth Goal:</span>
                    <span className="text-4xl font-bold text-blue-600">
                      {formatCurrency(wealthGap.wealth_goal_amount || 0)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Your target wealth goal at exit
                  </p>
                </div>

                {/* Current Personal Net Worth */}
                <div className="bg-white rounded-lg p-6 border-2 border-emerald-300">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700 font-semibold text-lg">Current Personal Net Worth:</span>
                    <span className="text-4xl font-bold text-emerald-600">
                      {formatCurrency(wealthGap.non_business_net_worth || 0)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Your current net worth excluding business value
                  </p>
                </div>

                {/* Gap Calculation */}
                {(() => {
                  const wealthGoal = wealthGap.wealth_goal_amount || 0;
                  const personalNetWorth = wealthGap.non_business_net_worth || 0;
                  const gap = wealthGoal - personalNetWorth;
                  const hasGap = gap > 0;

                  return (
                    <div className={`bg-white rounded-lg p-6 border-2 ${hasGap ? 'border-orange-300' : 'border-green-300'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-semibold text-lg">{hasGap ? 'Wealth Gap:' : 'Wealth Surplus:'}</span>
                        <span className={`text-4xl font-bold ${hasGap ? 'text-orange-600' : 'text-green-600'}`}>
                          {formatCurrency(Math.abs(gap))}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        {hasGap
                          ? 'Amount needed from business and other sources to reach your wealth goal'
                          : 'You already exceed your wealth goal! Any business sale will add to your surplus.'}
                      </p>
                    </div>
                  );
                })()}

                {/* Timeline */}
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <h5 className="font-semibold text-purple-900 mb-3">Timeline & Key Details</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <p className="text-xs text-purple-700">Current Age</p>
                      <p className="text-lg font-bold text-purple-900">{wealthGap.current_age}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-700">Exit Year</p>
                      <p className="text-lg font-bold text-purple-900">{wealthGap.exit_year}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-700">Exit Age</p>
                      <p className="text-lg font-bold text-purple-900">{ageAtExit}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-700">Years Until Exit</p>
                      <p className="text-lg font-bold text-purple-900">{yearsUntilExit}</p>
                    </div>
                  </div>
                </div>

                {/* What This Means For You */}
                {(() => {
                  const wealthGoal = wealthGap.wealth_goal_amount || 0;
                  const personalNetWorth = wealthGap.non_business_net_worth || 0;
                  const gap = wealthGoal - personalNetWorth;
                  const hasGap = gap > 0;

                  return (
                    <div className={`rounded-lg p-6 border-2 ${hasGap ? 'bg-orange-50 border-orange-300' : 'bg-green-50 border-green-300'}`}>
                      <h5 className={`font-bold text-lg mb-3 flex items-center gap-2 ${hasGap ? 'text-orange-900' : 'text-green-900'}`}>
                        <Target size={20} />
                        What This Means For You
                      </h5>
                      {hasGap ? (
                        <div className="space-y-3 text-sm">
                          <p className="text-gray-700">
                            To reach your wealth goal of <strong className="text-blue-700">{formatCurrency(wealthGoal)}</strong> by {wealthGap.exit_year},
                            you need to generate <strong className="text-orange-700">{formatCurrency(gap)}</strong> from your business sale and/or other investments.
                          </p>
                          <div className="bg-white/70 rounded-lg p-3 border border-orange-200">
                            <p className="text-xs text-orange-800 font-semibold mb-2">Required After-Tax Proceeds from Business:</p>
                            <p className="text-2xl font-bold text-orange-700">{formatCurrency(gap)}</p>
                            <p className="text-xs text-orange-600 mt-2">
                              This is the minimum your business should provide after taxes to reach your {formatCurrency(wealthGoal)} wealth goal
                            </p>
                          </div>
                          <p className="text-xs text-gray-600 italic">
                            Note: This is a simplified calculation. Consider consulting with a financial advisor for a comprehensive analysis
                            that accounts for asset growth, inflation, and tax optimization strategies.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3 text-sm">
                          <p className="text-green-800 font-semibold">
                            🎉 Congratulations! Your current personal net worth of <strong>{formatCurrency(personalNetWorth)}</strong> already
                            exceeds your wealth goal of <strong>{formatCurrency(wealthGoal)}</strong> by <strong>{formatCurrency(Math.abs(gap))}</strong>!
                          </p>
                          <div className="bg-white/70 rounded-lg p-3 border border-green-200">
                            <p className="text-xs text-green-800 mb-2">This means:</p>
                            <ul className="space-y-1 text-xs text-green-700">
                              <li>✓ You've already achieved your wealth goal</li>
                              <li>✓ Any business sale proceeds will be additional wealth</li>
                              <li>✓ You have flexibility in exit timing and terms</li>
                              <li>✓ Consider opportunities for legacy planning or philanthropy</li>
                            </ul>
                          </div>
                          <p className="text-xs text-gray-600 italic">
                            Tip: You may want to explore the "Monthly Needs" calculation method to ensure your wealth can support
                            your desired lifestyle throughout retirement.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : hasRequiredData ? (
              <div className="space-y-4">
                {localWealthSurplus > 0 ? (
                  <div className="bg-white rounded-lg p-6 border-2 border-green-300">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700 font-semibold text-lg">Wealth Surplus:</span>
                      <span className="text-4xl font-bold text-green-600">
                        {formatCurrency(localWealthSurplus)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      Great news! Your personal net worth at exit may exceed your lifestyle needs. The business sale may add to your wealth, but may not be required to sustain your lifestyle.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-6 border-2 border-orange-300">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700 font-semibold text-lg">Required from Business Sale:</span>
                      <span className="text-4xl font-bold text-orange-600">
                            {formatCurrency(localNetGapFromBusiness)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      After accounting for your personal net worth at exit, this is what your business may need to sell for to support your post-exit lifestyle goals.
                    </p>
                  </div>
                )}

                {futureBusinessValueDisplay > 0 && (
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Future Business Value at Exit:</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {formatCurrency(futureBusinessValueDisplay)}
                      </span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      {localWealthSurplus > 0 ? (
                        <p className="text-sm text-green-600 font-semibold">
                          ✓ Your personal net worth may cover your needs. This future business value of {formatCurrency(futureBusinessValueDisplay)} may be additional wealth!
                        </p>
                      ) : futureBusinessValueDisplay >= localRequiredSalePrice ? (
                        <p className="text-sm text-green-600 font-semibold">
                              ✓ Your future business value exceeds the required amount by {formatCurrency(futureBusinessValueDisplay - localNetGapFromBusiness)}
                        </p>
                      ) : (
                        <p className="text-sm text-red-600 font-semibold">
                                ⚠ Your future business value is {formatCurrency(localNetGapFromBusiness - futureBusinessValueDisplay)} below the required amount
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-xs text-blue-700 mb-1">Total Capital Needed</p>
                    <p className="text-lg font-bold text-blue-900">{formatCurrency(requiredData.total_capital_needed || 0)}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                    <p className="text-xs text-emerald-700 mb-1">Future Net Worth (Personal)</p>
                    <p className="text-lg font-bold text-emerald-900">{formatCurrency(futureNetWorth || 0)}</p>
                  </div>
                  <div className={`rounded-lg p-3 border ${localWealthSurplus > 0 ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                    <p className={`text-xs mb-1 ${localWealthSurplus > 0 ? 'text-green-700' : 'text-orange-700'}`}>
                      {localWealthSurplus > 0 ? 'Wealth Surplus' : 'Gap from Business'}
                    </p>
                    <p className={`text-lg font-bold ${localWealthSurplus > 0 ? 'text-green-900' : 'text-orange-900'}`}>
                      {formatCurrency(localWealthSurplus > 0 ? localWealthSurplus : localNetGapFromBusiness || 0)}
                    </p>
                  </div>
                </div>

                {/* Business Sale Parameters */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Your Share</p>
                    <p className="text-lg font-bold text-gray-900">{wealthGap.ownership_percentage}%</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Tax Rate</p>
                    <p className="text-lg font-bold text-gray-900">{wealthGap.tax_rate_on_sale}%</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">After-Tax Proceeds Needed</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(afterTaxProceeds)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Exit Year</p>
                    <p className="text-lg font-bold text-gray-900">{wealthGap.exit_year}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <p className="text-sm text-gray-600">
                  Complete the wealth gap calculator with your monthly needs to see your comprehensive analysis.
                </p>
              </div>
            )}
          </div>

          {/* Note */}
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
            <h4 className="font-bold text-lg text-rose-900 mb-3">Disclosure & Disclaimer</h4>
            <p className="text-xs text-rose-600">
              This analysis calculates total capital needed and accounts for your personal net worth growth to determine what your business must provide.
              The calculation may not fit all situations or local market conditions.
              This is a best-effort, automated projection based on the assumptions above.
              <strong> Consult with a financial advisor and/or accountant for personalized guidance.</strong>
            </p>
          </div>

          {/* Assumptions Section - Collapsible */}
          {wealthGap.wealth_goal_method === 'monthly_needs' && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('assumptions')}
                className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition"
              >
                <h4 className="font-bold text-lg text-gray-900 flex items-center gap-2">
                  <Calendar className="text-blue-600" size={20} />
                  Calculation Assumptions
                </h4>
                {expandedSections.assumptions ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              {expandedSections.assumptions && (
                <div className="p-6 space-y-4">
                  <p className="text-sm text-gray-700 mb-4">
                    The required sale price calculation is based on the following assumptions and simplifications:
                  </p>

                  {/* Timeline - Full Width */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <h5 className="font-semibold text-purple-900 mb-3">Timeline</h5>
                    <ul className="space-y-2 text-sm text-purple-800">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Current age: <strong>{wealthGap.current_age}</strong></span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Retirement age: <strong>{wealthGap.retirement_age}</strong></span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Life expectancy: <strong>{wealthGap.life_expectancy}</strong></span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Exit year: <strong>{wealthGap.exit_year}</strong> (age {ageAtExit})</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Planning for <strong>{wealthGap.life_expectancy - ageAtExit} years</strong> post-exit</span>
                      </li>
                    </ul>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Current Lifestyle & Cash Flow */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h5 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        💰 Current Lifestyle & Cash Flow
                      </h5>
                      <div className="space-y-3 text-sm">
                        {/* Total Monthly Need */}
                        <div className="bg-white/70 rounded-lg p-3 border border-blue-300">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-blue-800 font-semibold">Total Monthly Cash Need:</span>
                            <span className="text-xl font-bold text-blue-900">{formatCurrency(wealthGap.monthly_cash_need)}</span>
                          </div>

                          {/* Allocation Breakdown */}
                          {(wealthGap.monthly_cash_need_from_non_business > 0 || wealthGap.monthly_cash_need_from_business > 0 || wealthGap.outside_income_monthly > 0) && (
                            <div className="mt-2 pt-2 border-t border-blue-200 space-y-1.5">
                              <p className="text-xs text-blue-700 font-semibold mb-1.5">How this is allocated:</p>

                              {wealthGap.monthly_cash_need_from_non_business > 0 && (
                                <div className="flex justify-between items-center text-xs bg-purple-50 rounded px-2 py-1">
                                  <span className="text-purple-800">
                                    Non-Business Net Worth ({wealthGap.non_business_source_category?.replace('_', ' ') || 'other assets'}):
                                  </span>
                                  <span className="font-bold text-purple-900">{formatCurrency(wealthGap.monthly_cash_need_from_non_business)}</span>
                                </div>
                              )}

                              {wealthGap.monthly_cash_need_from_business > 0 && (
                                <div className="flex justify-between items-center text-xs bg-blue-50 rounded px-2 py-1">
                                  <span className="text-blue-800">From Business (Owner's Draw/Salary):</span>
                                  <span className="font-bold text-blue-900">{formatCurrency(wealthGap.monthly_cash_need_from_business)}</span>
                                </div>
                              )}

                              {wealthGap.outside_income_monthly > 0 && (
                                <div className="flex justify-between items-center text-xs bg-green-50 rounded px-2 py-1">
                                  <span className="text-green-800">Outside Income Sources:</span>
                                  <span className="font-bold text-green-900">{formatCurrency(wealthGap.outside_income_monthly)}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Adjustments for Exit */}
                        <div className="space-y-1.5">
                          <div className="flex justify-between items-center text-xs bg-white/70 rounded px-2 py-1.5">
                            <span className="text-blue-700">Lifestyle change at exit:</span>
                            <span className="font-bold text-blue-900">{wealthGap.lifestyle_change_percentage > 0 ? '+' : ''}{wealthGap.lifestyle_change_percentage}%</span>
                          </div>
                          <div className="flex justify-between items-center text-xs bg-white/70 rounded px-2 py-1.5">
                            <span className="text-blue-700">Annual inflation rate:</span>
                            <span className="font-bold text-blue-900">{wealthGap.annual_inflation_rate}%</span>
                          </div>
                        </div>

                        {/* Monthly Need at Exit */}
                        <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg p-3 border-2 border-blue-400">
                          <div className="flex justify-between items-center">
                            <span className="text-blue-900 font-semibold">Monthly need at exit:</span>
                            <span className="text-xl font-bold text-blue-700">{formatCurrency(monthlyNeedAtExit)}</span>
                          </div>
                          <p className="text-xs text-blue-600 mt-1">
                            Projected {yearsUntilExit} years forward with inflation & lifestyle changes
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Outside Income */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                        💵 Outside Income Sources
                      </h5>
                      <div className="space-y-3 text-sm">
                        {/* Current Outside Income */}
                        <div className="bg-white/70 rounded-lg p-3 border border-green-300">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-green-800 font-semibold">Current Monthly Income:</span>
                            <span className="text-xl font-bold text-green-900">{formatCurrency(wealthGap.outside_income_monthly || 0)}</span>
                          </div>
                          <p className="text-xs text-green-600">
                            Social Security, pensions, rental income, spouse's income, etc.
                          </p>
                        </div>

                        {/* Growth Rate */}
                        <div className="bg-white/70 rounded-lg p-3 border border-green-300">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-green-800 font-semibold">Annual Growth Rate:</span>
                            <span className="text-lg font-bold text-green-900">{wealthGap.outside_income_annual_increase}%</span>
                          </div>
                          <p className="text-xs text-green-600">
                            Expected COLA adjustments, pension increases, etc.
                          </p>
                        </div>

                        {/* Future Value at Exit */}
                        <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-3 border-2 border-green-400">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-green-900 font-semibold">At Exit ({wealthGap.exit_year}):</span>
                            <span className="text-xl font-bold text-green-700">{formatCurrency(futureOutsideIncomeMonthly)}</span>
                          </div>
                          <p className="text-xs text-green-600">
                            After {yearsUntilRetirement} years @ {wealthGap.outside_income_annual_increase}% growth
                          </p>
                        </div>

                        {/* Net Monthly Need Calculation */}
                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-300 mt-3">
                          <p className="text-xs text-blue-700 font-semibold mb-2">Net Monthly Need:</p>
                          <div className="space-y-1">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-blue-700">Current total need:</span>
                              <span className="font-semibold">{formatCurrency(totalCurrentMonthlyNeed)}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-blue-700">Less outside income:</span>
                              <span className="font-semibold text-green-700">- {formatCurrency(wealthGap.outside_income_monthly || 0)}</span>
                            </div>
                            <div className="border-t border-blue-300 pt-1 flex justify-between items-center">
                              <span className="text-blue-900 font-bold">Current net need:</span>
                              <span className="text-lg font-bold text-blue-700">{formatCurrency(currentNetMonthlyNeed)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Net Need at Exit */}
                        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg p-3 border border-emerald-300 mt-3">
                          <p className="text-xs text-emerald-700 font-semibold mb-2">Net need at exit:</p>
                          <div className="space-y-1">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-emerald-700">Monthly need at exit ({wealthGap.exit_year}):</span>
                              <span className="font-semibold">{formatCurrency(monthlyNeedAtExit)}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-emerald-700">Less outside income at exit:</span>
                              <span className="font-semibold text-green-700">- {formatCurrency(futureOutsideIncomeMonthly)}</span>
                            </div>
                            <div className="border-t border-emerald-300 pt-1 flex justify-between items-center">
                              <span className="text-emerald-900 font-bold">Net need at exit:</span>
                              <span className="text-lg font-bold text-emerald-700">{formatCurrency(netMonthlyNeed)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Personal Net Worth */}
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <h5 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                        💰 Personal Net Worth
                      </h5>
                      <div className="space-y-3 text-sm">
                        {/* Current Net Worth Summary */}
                        <div className="bg-white/70 rounded-lg p-3 border border-emerald-300">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-emerald-800 font-semibold">Current Total:</span>
                            <span className="text-xl font-bold text-emerald-900">{formatCurrency(wealthGap.current_net_worth)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-emerald-700 text-xs">Future at Exit ({wealthGap.exit_year}):</span>
                            <span className="text-lg font-bold text-green-700">{formatCurrency(futureNetWorth)}</span>
                          </div>
                        </div>

                        {/* Asset Breakdown */}
                        <div className="space-y-2">
                          {/* Liquid Assets */}
                          <div className={`rounded-lg p-2 ${nonBusinessSourceCategory === 'liquid_assets' ? 'bg-purple-100 border-2 border-purple-400' : 'bg-white/50'}`}>
                            <div className="flex justify-between items-start mb-1">
                              <div className="flex-1">
                                <span className="text-emerald-900 font-semibold">Liquid Assets</span>
                                {nonBusinessSourceCategory === 'liquid_assets' && monthlyWithdrawal > 0 && (
                                  <div className="text-xs text-purple-700 font-bold mt-0.5">
                                    ↓ {formatCurrency(monthlyWithdrawal)}/month withdrawn
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-emerald-800 font-semibold">{formatCurrency(wealthGap.liquid_assets || 0)}</div>
                                <div className="text-xs text-emerald-600">@ {liquidAssetsRate}%</div>
                              </div>
                            </div>
                            <div className="flex justify-between items-center text-xs pt-1 border-t border-emerald-200">
                              <span className="text-emerald-700">Future Value:</span>
                              <span className="font-bold text-green-700">{formatCurrency(liquidAssetsFV)}</span>
                            </div>
                          </div>

                          {/* Retirement Accounts */}
                          <div className={`rounded-lg p-2 ${nonBusinessSourceCategory === 'retirement_accounts' ? 'bg-purple-100 border-2 border-purple-400' : 'bg-white/50'}`}>
                            <div className="flex justify-between items-start mb-1">
                              <div className="flex-1">
                                <span className="text-emerald-900 font-semibold">Retirement Accounts</span>
                                {nonBusinessSourceCategory === 'retirement_accounts' && monthlyWithdrawal > 0 && (
                                  <div className="text-xs text-purple-700 font-bold mt-0.5">
                                    ↓ {formatCurrency(monthlyWithdrawal)}/month withdrawn
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-emerald-800 font-semibold">{formatCurrency(wealthGap.retirement_accounts || 0)}</div>
                                <div className="text-xs text-emerald-600">@ {retirementAccountsRate}%</div>
                              </div>
                            </div>
                            <div className="flex justify-between items-center text-xs pt-1 border-t border-emerald-200">
                              <span className="text-emerald-700">Future Value:</span>
                              <span className="font-bold text-green-700">{formatCurrency(retirementAccountsFV)}</span>
                            </div>
                          </div>

                          {/* Real Estate */}
                          <div className={`rounded-lg p-2 ${nonBusinessSourceCategory === 'real_estate' ? 'bg-purple-100 border-2 border-purple-400' : 'bg-white/50'}`}>
                            <div className="flex justify-between items-start mb-1">
                              <div className="flex-1">
                                <span className="text-emerald-900 font-semibold">Real Estate Equity</span>
                                {nonBusinessSourceCategory === 'real_estate' && monthlyWithdrawal > 0 && (
                                  <div className="text-xs text-purple-700 font-bold mt-0.5">
                                    ↓ {formatCurrency(monthlyWithdrawal)}/month withdrawn
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-emerald-800 font-semibold">{formatCurrency(wealthGap.real_estate_equity || 0)}</div>
                                <div className="text-xs text-emerald-600">@ {realEstateRate}%</div>
                              </div>
                            </div>
                            <div className="flex justify-between items-center text-xs pt-1 border-t border-emerald-200">
                              <span className="text-emerald-700">Future Value:</span>
                              <span className="font-bold text-green-700">{formatCurrency(realEstateFV)}</span>
                            </div>
                          </div>

                          {/* Other Investments */}
                          <div className={`rounded-lg p-2 ${nonBusinessSourceCategory === 'other_investments' ? 'bg-purple-100 border-2 border-purple-400' : 'bg-white/50'}`}>
                            <div className="flex justify-between items-start mb-1">
                              <div className="flex-1">
                                <span className="text-emerald-900 font-semibold">Other Investments</span>
                                {nonBusinessSourceCategory === 'other_investments' && monthlyWithdrawal > 0 && (
                                  <div className="text-xs text-purple-700 font-bold mt-0.5">
                                    ↓ {formatCurrency(monthlyWithdrawal)}/month withdrawn
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-emerald-800 font-semibold">{formatCurrency(wealthGap.other_investments || 0)}</div>
                                <div className="text-xs text-emerald-600">@ {otherInvestmentsRate}%</div>
                              </div>
                            </div>
                            <div className="flex justify-between items-center text-xs pt-1 border-t border-emerald-200">
                              <span className="text-emerald-700">Future Value:</span>
                              <span className="font-bold text-green-700">{formatCurrency(otherInvestmentsFV)}</span>
                            </div>
                          </div>

                          {/* Liabilities */}
                          {(wealthGap.total_liabilities || 0) > 0 && (
                            <div className="rounded-lg p-2 bg-red-50">
                              <div className="flex justify-between items-start mb-1">
                                <div className="flex-1">
                                  <span className="text-red-900 font-semibold">Total Liabilities</span>
                                </div>
                                <div className="text-right">
                                  <div className="text-red-800 font-semibold">{formatCurrency(wealthGap.total_liabilities || 0)}</div>
                                  <div className="text-xs text-red-600">@ {wealthGap.liability_reduction_rate || 0}% reduction</div>
                                </div>
                              </div>
                              <div className="flex justify-between items-center text-xs pt-1 border-t border-red-200">
                                <span className="text-red-700">Future Value:</span>
                                <span className="font-bold text-red-700">{formatCurrency(futureLiabilities)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Total Capital Needed */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h5 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        💎 Total Capital Needed
                      </h5>
                      <div className="space-y-3 text-sm">
                        <p className="text-xs text-blue-700 mb-2">
                          Present Value Calculation:
                        </p>

                        {/* Calculation Parameters */}
                        <div className="space-y-1.5">
                          <div className="flex justify-between items-center text-xs bg-white/70 rounded px-2 py-1.5">
                            <span className="text-blue-700">Net Monthly Need at Exit:</span>
                            <span className="font-bold text-blue-900">{formatCurrency(netMonthlyNeed)}</span>
                          </div>
                          <div className="flex justify-between items-center text-xs bg-white/70 rounded px-2 py-1.5">
                            <span className="text-blue-700">Years to Fund:</span>
                            <span className="font-bold text-blue-900">{wealthGap.life_expectancy - ageAtExit} years (age {ageAtExit} to {wealthGap.life_expectancy})</span>
                          </div>
                          <div className="flex justify-between items-center text-xs bg-white/70 rounded px-2 py-1.5">
                            <span className="text-blue-700">Investment Return:</span>
                            <span className="font-bold text-blue-900">{wealthGap.annual_return_rate}% annually</span>
                          </div>
                          <div className="flex justify-between items-center text-xs bg-white/70 rounded px-2 py-1.5">
                            <span className="text-blue-700">Inflation Rate:</span>
                            <span className="font-bold text-blue-900">{wealthGap.annual_inflation_rate}% annually</span>
                          </div>
                        </div>

                        {/* Total Capital Needed Result */}
                        <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg p-3 border-2 border-blue-400">
                          <div className="flex justify-between items-center">
                            <span className="text-blue-900 font-semibold">Total Capital Needed:</span>
                            <span className="text-xl font-bold text-blue-700">{formatCurrency(totalCapitalNeeded)}</span>
                          </div>
                          <p className="text-xs text-blue-600 mt-2 italic">
                            This is the lump sum needed at exit to fund all future monthly needs, accounting for investment growth and inflation.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Note */}
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 mt-4">
                    <p className="text-xs text-rose-600">
                      <strong>Note:</strong> This analysis calculates total capital needed and accounts for your personal net worth growth to determine what your business must provide.
                      The calculation may not fit all situations or local market conditions.
                      This is a best-effort, automated projection based on the assumptions above.
                      <strong> Consult with a financial advisor and/or accountant for personalized guidance.</strong>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Data Flow and Calculations - Collapsible */}
          {wealthGap.wealth_goal_method === 'monthly_needs' && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('dataFlow')}
                className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition"
              >
                <h4 className="font-bold text-lg text-gray-900 flex items-center gap-2">
                  <TrendingUp className="text-emerald-600" size={20} />
                  Detailed Calculation Flow
                </h4>
                {expandedSections.dataFlow ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              {expandedSections.dataFlow && (
                <div className="p-6 space-y-6">
                  <p className="text-sm text-gray-700 mb-4">
                    Here's how your current financial data projects into the future and determines your required sale price:
                  </p>

                  {/* Step 1: Current Lifestyle Needs */}
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-500 rounded-lg p-4">
                    <h5 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                      Current Monthly Lifestyle Needs
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-blue-800">Current Monthly Cash Need:</span>
                        <span className="font-bold text-blue-900">{formatCurrency(wealthGap.monthly_cash_need)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-blue-800">Current Monthly Outside Income:</span>
                        <span className="font-bold text-green-700">- {formatCurrency(wealthGap.outside_income_monthly || 0)}</span>
                      </div>
                      <div className="border-t-2 border-blue-300 pt-2 flex justify-between items-center">
                        <span className="text-blue-900 font-semibold">Current Net Monthly Need:</span>
                        <span className="font-bold text-xl text-blue-700">{formatCurrency(currentNetMonthlyNeed)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Projection to Exit Year */}
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 border-l-4 border-purple-500 rounded-lg p-4">
                    <h5 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                      <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                      Projection to Exit Year ({wealthGap.exit_year})
                    </h5>
                    <div className="space-y-3 text-sm">
                      {/* Monthly Cash Need Projection */}
                      <div className="bg-white/50 rounded p-3">
                        <p className="text-purple-800 font-semibold mb-2">Monthly Cash Need Growth:</p>
                        <div className="space-y-1 ml-4">
                          <div className="flex justify-between">
                            <span className="text-purple-700">Starting Point:</span>
                            <span className="font-semibold">{formatCurrency(wealthGap.monthly_cash_need)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-purple-700">× Inflation Factor ({wealthGap.annual_inflation_rate}% over {yearsUntilExit} years):</span>
                            <span className="font-semibold">{inflationMultiplier.toFixed(5)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-purple-700">× Lifestyle Change ({wealthGap.lifestyle_change_percentage > 0 ? '+' : ''}{wealthGap.lifestyle_change_percentage}%):</span>
                            <span className="font-semibold">{(1 + wealthGap.lifestyle_change_percentage / 100).toFixed(5)}</span>
                          </div>
                          <div className="border-t border-purple-300 pt-1 flex justify-between">
                            <span className="text-purple-900 font-semibold">= Monthly Need at Exit:</span>
                            <span className="font-bold text-purple-900">{formatCurrency(monthlyNeedAtExit)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Outside Income Projection */}
                      <div className="bg-white/50 rounded p-3">
                        <p className="text-purple-800 font-semibold mb-2">Outside Income Growth:</p>
                        <div className="space-y-1 ml-4">
                          <div className="flex justify-between">
                            <span className="text-purple-700">Starting Point:</span>
                            <span className="font-semibold">{formatCurrency(wealthGap.outside_income_monthly || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-purple-700">× Growth Factor ({wealthGap.outside_income_annual_increase}% over {yearsUntilRetirement} years):</span>
                            <span className="font-semibold">{Math.pow(1 + outsideIncomeGrowthRate, yearsUntilRetirement).toFixed(5)}</span>
                          </div>
                          <div className="border-t border-purple-300 pt-1 flex justify-between">
                            <span className="text-purple-900 font-semibold">= Outside Income at Exit:</span>
                            <span className="font-bold text-green-700">{formatCurrency(futureOutsideIncomeMonthly)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Net Monthly Need at Exit */}
                      <div className="bg-gradient-to-r from-emerald-100 to-teal-100 rounded p-3 border-2 border-emerald-500">
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-emerald-800">Monthly Need at Exit:</span>
                            <span className="font-bold">{formatCurrency(monthlyNeedAtExit)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-emerald-800">Outside Income at Exit:</span>
                            <span className="font-bold text-green-700">- {formatCurrency(futureOutsideIncomeMonthly)}</span>
                          </div>
                          <div className="border-t-2 border-emerald-600 pt-2 flex justify-between items-center">
                            <span className="text-emerald-900 font-bold">Net Monthly Need at Exit:</span>
                            <span className="font-bold text-2xl text-emerald-700">{formatCurrency(netMonthlyNeed)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 3: Total Capital Needed */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-100 border-l-4 border-blue-500 rounded-lg p-4">
                    <h5 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                      Total Capital Needed
                    </h5>
                    <div className="space-y-3 text-sm">
                      <div className="bg-white/50 rounded p-3">
                        <p className="text-blue-800 font-semibold mb-2">Present Value Calculation:</p>
                        <div className="space-y-1 ml-4">
                          <div className="flex justify-between">
                            <span className="text-blue-700">Net Monthly Need at Exit:</span>
                            <span className="font-semibold">{formatCurrency(netMonthlyNeed)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700">Years to Fund:</span>
                            <span className="font-semibold">{wealthGap.life_expectancy - ageAtExit} years (age {ageAtExit} to {wealthGap.life_expectancy})</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700">Investment Return:</span>
                            <span className="font-semibold">{wealthGap.annual_return_rate}% annually</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700">Inflation Rate:</span>
                            <span className="font-semibold">{wealthGap.annual_inflation_rate}% annually</span>
                          </div>
                          <div className="border-t border-blue-300 pt-1 flex justify-between">
                            <span className="text-blue-900 font-semibold">Total Capital Needed:</span>
                            <span className="font-bold text-blue-900">{formatCurrency(hasRequiredData ? requiredData.total_capital_needed : 0)}</span>
                          </div>
                        </div>
                        <p className="text-xs text-blue-600 mt-2 italic">
                          This is the lump sum needed at exit to fund all future monthly needs, accounting for investment growth and inflation.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 4: Personal Asset Growth - Phase 1 (Pre-Exit) */}
                  <div className="bg-gradient-to-r from-teal-50 to-cyan-100 border-l-4 border-teal-500 rounded-lg p-4">
                    <h5 className="font-bold text-teal-900 mb-3 flex items-center gap-2">
                      <span className="bg-teal-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs">4</span>
                      Personal Asset Growth - Phase 1 (Pre-Exit)
                    </h5>
                    <div className="space-y-3 text-sm">
                      <p className="text-teal-800 mb-2">
                        Your personal assets grow from now until exit ({yearsUntilExit} years) at their individual return rates:
                      </p>

                      {/* Liquid Assets */}
                      <div className="bg-white/70 rounded p-3 border border-teal-200">
                        <p className="text-teal-900 font-semibold mb-2">Liquid Assets:</p>
                        <div className="space-y-1 ml-4 text-xs">
                          <div className="flex justify-between">
                            <span className="text-teal-700">Current balance:</span>
                            <span className="font-semibold">{formatCurrency(wealthGap.liquid_assets || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-teal-700">Annual return rate:</span>
                            <span className="font-semibold">{liquidAssetsRate}%</span>
                          </div>
                          {monthlyWithdrawal > 0 && nonBusinessSourceCategory === 'liquid_assets' && (
                            <div className="flex justify-between">
                              <span className="text-teal-700">Monthly withdrawal growing with inflation (pre-exit):</span>
                              <span className="font-semibold text-purple-700">-{formatCurrency(monthlyWithdrawal)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-teal-700">Growth period:</span>
                            <span className="font-semibold">{yearsUntilRetirement} years</span>
                          </div>
                          <div className="border-t border-teal-300 pt-1 flex justify-between">
                            <span className="text-teal-900 font-bold">Value at exit:</span>
                            <span className="font-bold text-green-700">{formatCurrency(liquidAssetsFV)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Retirement Accounts */}
                      <div className="bg-white/70 rounded p-3 border border-teal-200">
                        <p className="text-teal-900 font-semibold mb-2">Retirement Accounts:</p>
                        <div className="space-y-1 ml-4 text-xs">
                          <div className="flex justify-between">
                            <span className="text-teal-700">Current balance:</span>
                            <span className="font-semibold">{formatCurrency(wealthGap.retirement_accounts || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-teal-700">Annual return rate:</span>
                            <span className="font-semibold">{retirementAccountsRate}%</span>
                          </div>
                          {monthlyWithdrawal > 0 && nonBusinessSourceCategory === 'retirement_accounts' && (
                            <div className="flex justify-between">
                              <span className="text-teal-700">Monthly withdrawal growing with inflation (pre-exit):</span>
                              <span className="font-semibold text-purple-700">-{formatCurrency(monthlyWithdrawal)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-teal-700">Growth period:</span>
                            <span className="font-semibold">{yearsUntilRetirement} years</span>
                          </div>
                          <div className="border-t border-teal-300 pt-1 flex justify-between">
                            <span className="text-teal-900 font-bold">Value at exit:</span>
                            <span className="font-bold text-green-700">{formatCurrency(retirementAccountsFV)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Real Estate */}
                      <div className="bg-white/70 rounded p-3 border border-teal-200">
                        <p className="text-teal-900 font-semibold mb-2">Real Estate Equity:</p>
                        <div className="space-y-1 ml-4 text-xs">
                          <div className="flex justify-between">
                            <span className="text-teal-700">Current equity:</span>
                            <span className="font-semibold">{formatCurrency(wealthGap.real_estate_equity || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-teal-700">Annual appreciation rate:</span>
                            <span className="font-semibold">{realEstateRate}%</span>
                          </div>
                          {monthlyWithdrawal > 0 && nonBusinessSourceCategory === 'real_estate' && (
                            <div className="flex justify-between">
                              <span className="text-teal-700">Monthly withdrawal growing with inflation (pre-exit):</span>
                              <span className="font-semibold text-purple-700">-{formatCurrency(monthlyWithdrawal)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-teal-700">Growth period:</span>
                            <span className="font-semibold">{yearsUntilRetirement} years</span>
                          </div>
                          <div className="border-t border-teal-300 pt-1 flex justify-between">
                            <span className="text-teal-900 font-bold">Value at exit:</span>
                            <span className="font-bold text-green-700">{formatCurrency(realEstateFV)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Other Investments */}
                      <div className="bg-white/70 rounded p-3 border border-teal-200">
                        <p className="text-teal-900 font-semibold mb-2">Other Investments:</p>
                        <div className="space-y-1 ml-4 text-xs">
                          <div className="flex justify-between">
                            <span className="text-teal-700">Current balance:</span>
                            <span className="font-semibold">{formatCurrency(wealthGap.other_investments || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-teal-700">Annual return rate:</span>
                            <span className="font-semibold">{otherInvestmentsRate}%</span>
                          </div>
                          {monthlyWithdrawal > 0 && nonBusinessSourceCategory === 'other_investments' && (
                            <div className="flex justify-between">
                              <span className="text-teal-700">Monthly withdrawal growing with inflation (pre-exit):</span>
                              <span className="font-semibold text-purple-700">-{formatCurrency(monthlyWithdrawal)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-teal-700">Growth period:</span>
                            <span className="font-semibold">{yearsUntilRetirement} years</span>
                          </div>
                          <div className="border-t border-teal-300 pt-1 flex justify-between">
                            <span className="text-teal-900 font-bold">Value at exit:</span>
                            <span className="font-bold text-green-700">{formatCurrency(otherInvestmentsFV)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Liabilities */}
                      {(wealthGap.total_liabilities || 0) > 0 && (
                        <div className="bg-red-50 rounded p-3 border border-red-200">
                          <p className="text-red-900 font-semibold mb-2">Total Liabilities:</p>
                          <div className="space-y-1 ml-4 text-xs">
                            <div className="flex justify-between">
                              <span className="text-red-700">Current balance:</span>
                              <span className="font-semibold">{formatCurrency(wealthGap.total_liabilities || 0)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-red-700">Annual reduction rate:</span>
                              <span className="font-semibold">{wealthGap.liability_reduction_rate || 0}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-red-700">Paydown period:</span>
                              <span className="font-semibold">{yearsUntilRetirement} years</span>
                            </div>
                            <div className="border-t border-red-300 pt-1 flex justify-between">
                              <span className="text-red-900 font-bold">Balance at exit:</span>
                              <span className="font-bold text-red-700">{formatCurrency(futureLiabilities)}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Total Pre-Exit Growth */}
                      <div className="bg-gradient-to-r from-emerald-100 to-teal-200 rounded p-3 border-2 border-emerald-500">
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-emerald-900 font-bold">Total Assets at Exit:</span>
                            <span className="font-bold text-xl text-emerald-700">
                              {formatCurrency(liquidAssetsFV + retirementAccountsFV + realEstateFV + otherInvestmentsFV)}
                            </span>
                          </div>
                          {(wealthGap.total_liabilities || 0) > 0 && (
                            <>
                              <div className="flex justify-between items-center">
                                <span className="text-red-800">Less Liabilities:</span>
                                <span className="font-semibold text-red-700">-{formatCurrency(futureLiabilities)}</span>
                              </div>
                              <div className="border-t-2 border-emerald-600 pt-1 flex justify-between items-center">
                                <span className="text-emerald-900 font-bold">Net Worth at Exit:</span>
                                <span className="font-bold text-2xl text-emerald-700">{formatCurrency(futureNetWorth)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 5: Gap Analysis - Capital Needed vs Net Worth at Exit */}
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-100 border-l-4 border-emerald-500 rounded-lg p-4">
                    <h5 className="font-bold text-emerald-900 mb-3 flex items-center gap-2">
                      <span className="bg-emerald-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">5</span>
                      Gap Analysis: Personal Net Worth vs Capital Needed
                    </h5>
                    <div className="space-y-3 text-sm">
                      <p className="text-emerald-800 mb-2">
                        Now we compare your future net worth (from Step 4) against the total capital needed (from Step 3):
                      </p>

                      {/* Reference to Step 4 */}
                      <div className="bg-white/70 rounded p-3 border border-teal-300">
                        <p className="text-teal-900 font-semibold mb-2">From Step 4 - Your Net Worth at Exit:</p>
                        <div className="space-y-1 ml-4 text-xs">
                          <div className="flex justify-between">
                            <span className="text-teal-700">Total Assets at Exit:</span>
                            <span className="font-semibold">
                              {formatCurrency(liquidAssetsFV + retirementAccountsFV + realEstateFV + otherInvestmentsFV)}
                            </span>
                          </div>
                          {(wealthGap.total_liabilities || 0) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-teal-700">Less Liabilities:</span>
                              <span className="font-semibold text-red-700">-{formatCurrency(futureLiabilities)}</span>
                            </div>
                          )}
                          <div className="border-t border-teal-300 pt-1 flex justify-between">
                            <span className="text-teal-900 font-bold">Net Worth at Exit:</span>
                            <span className="font-bold text-emerald-900">{formatCurrency(futureNetWorth)}</span>
                          </div>
                        </div>
                        <p className="text-xs text-teal-600 mt-2 italic">
                          ✓ Calculated from individual asset growth in Step 4
                        </p>
                      </div>

                      {/* Reference to Step 3 */}
                      <div className="bg-white/70 rounded p-3 border border-blue-300">
                        <p className="text-blue-900 font-semibold mb-2">From Step 3 - Total Capital Needed:</p>
                        <div className="space-y-1 ml-4 text-xs">
                          <div className="flex justify-between">
                            <span className="text-blue-700">Net Monthly Need at Exit:</span>
                            <span className="font-semibold">{formatCurrency(netMonthlyNeed)}/month</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700">Years to Fund:</span>
                            <span className="font-semibold">{wealthGap.life_expectancy - ageAtExit} years</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700">Investment Return:</span>
                            <span className="font-semibold">{wealthGap.annual_return_rate}%</span>
                          </div>
                          <div className="border-t border-blue-300 pt-1 flex justify-between">
                            <span className="text-blue-900 font-bold">Total Capital Needed:</span>
                            <span className="font-bold text-blue-900">{formatCurrency(hasRequiredData ? requiredData.total_capital_needed : 0)}</span>
                          </div>
                        </div>
                        <p className="text-xs text-blue-600 mt-2 italic">
                          ✓ Present value of all future spending needs
                        </p>
                      </div>

                      {/* Net Gap Calculation */}
                      <div className={`rounded p-3 border-2 ${hasRequiredData && localWealthSurplus > 0 ? 'bg-gradient-to-r from-green-100 to-emerald-200 border-green-500' : 'bg-gradient-to-r from-orange-100 to-red-200 border-orange-500'}`}>
                        <p className="font-semibold mb-2 text-sm">
                          {hasRequiredData && localWealthSurplus > 0 ? 'Surplus Calculation:' : 'Gap Calculation:'}
                        </p>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between items-center">
                            <span className={hasRequiredData && localWealthSurplus > 0 ? 'text-green-800' : 'text-orange-800'}>
                              Net Worth at Exit (Step 4):
                            </span>
                            <span className="font-bold">{formatCurrency(futureNetWorth)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className={hasRequiredData && localWealthSurplus > 0 ? 'text-green-800' : 'text-orange-800'}>
                              Total Capital Needed (Step 3):
                            </span>
                            <span className="font-bold">-{formatCurrency(hasRequiredData ? requiredData.total_capital_needed : 0)}</span>
                          </div>
                          <div className="border-t-2 border-current pt-2 flex justify-between items-center">
                            {hasRequiredData && localWealthSurplus > 0 ? (
                              <>
                                <span className="font-bold text-green-900 text-base">= Wealth Surplus:</span>
                                <span className="font-bold text-2xl text-green-700">{formatCurrency(localWealthSurplus)}</span>
                              </>
                            ) : (
                              <>
                                <span className="font-bold text-orange-900 text-base">= Gap from Business:</span>
                                <span className="font-bold text-2xl text-orange-700">{formatCurrency(hasRequiredData ? localNetGapFromBusiness : 0)}</span>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="mt-3 pt-2 border-t border-current">
                          {hasRequiredData && localWealthSurplus > 0 ? (
                            <p className="text-xs text-green-800">
                              <strong>✓ Great news!</strong> Your personal net worth may exceed your needs by this amount.
                              Any business sale proceeds will be additional wealth.
                            </p>
                          ) : (
                            <p className="text-xs text-orange-800">
                              <strong>⚠ Gap to fill:</strong> Your business needs to provide this amount in after-tax proceeds
                              to fully fund your lifestyle goals.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 6: Required Sale Price (if gap exists) */}
                  {hasRequiredData && localNetGapFromBusiness > 0 && (
                    <div className="bg-gradient-to-r from-orange-50 to-red-100 border-l-4 border-orange-500 rounded-lg p-4">
                      <h5 className="font-bold text-orange-900 mb-3 flex items-center gap-2">
                        <span className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">6</span>
                        Required Business Sale Price
                      </h5>
                      <div className="space-y-3 text-sm">
                        <div className="bg-gradient-to-r from-red-100 to-orange-200 rounded p-3 border-2 border-red-500">
                          <p className="text-red-900 font-semibold mb-2">Calculating Required Sale Price:</p>
                          <div className="space-y-1 ml-4">
                            <div className="flex justify-between">
                              <span className="text-red-800">Gap from Business (After-Tax):</span>
                              <span className="font-semibold">{formatCurrency(localNetGapFromBusiness)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-red-800">÷ Your Ownership Share:</span>
                              <span className="font-semibold">{wealthGap.ownership_percentage}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-red-800">÷ After-Tax Rate (1 - {wealthGap.tax_rate_on_sale}%):</span>
                              <span className="font-semibold">{((1 - taxDecimal) * 100).toFixed(1)}%</span>
                            </div>
                            <div className="border-t-2 border-red-600 pt-2 flex justify-between items-center">
                              <span className="text-red-900 font-bold text-lg">Required Sale Price:</span>
                              <span className="font-bold text-3xl text-red-700">{formatCurrency(localRequiredSalePrice)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 7: Business Value Comparison */}
                  {futureBusinessValueDisplay > 0 && hasRequiredData && (
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-l-4 border-gray-500 rounded-lg p-4">
                      <h5 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="bg-gray-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">7</span>
                        Business Value Analysis
                      </h5>
                      <div className="space-y-3 text-sm">
                        {/* Future Value Calculation */}
                        <div className="bg-white/50 rounded p-3">
                          <p className="text-gray-800 font-semibold mb-2">Future Business Value Projection:</p>
                          <div className="space-y-1 ml-4">
                            <div className="flex justify-between">
                              <span className="text-gray-700">Current Exit Value (PV):</span>
                              <span className="font-semibold text-blue-700">{formatCurrency(wealthGap.desired_sale_price || 0)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-700">Business Growth Rate:</span>
                              <span className="font-semibold">{wealthGap.business_growth_rate}% annually</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-700">Years to Exit:</span>
                              <span className="font-semibold">{yearsUntilExit} years</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-700">Growth Factor:</span>
                              <span className="font-semibold">{Math.pow(1 + (wealthGap.business_growth_rate || 0) / 100, yearsUntilExit).toFixed(5)}</span>
                            </div>
                            <div className="border-t border-gray-300 pt-1 flex justify-between">
                              <span className="text-gray-900 font-semibold">Future Value at Exit (FV):</span>
                              <span className="font-bold text-blue-600">{formatCurrency(futureBusinessValueDisplay)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Comparison */}
                        {localNetGapFromBusiness > 0 ? (
                          <>
                            <div className="bg-white/50 rounded p-3">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-700">Required from Business:</span>
                                <span className="font-bold text-orange-600 text-lg">{formatCurrency(localRequiredSalePrice)}</span>
                              </div>
                            </div>
                            <div className={`border-t-2 pt-2 flex justify-between items-center ${futureBusinessValueDisplay >= localRequiredSalePrice ? 'border-green-500' : 'border-red-500'}`}>
                              <span className="font-bold text-gray-900">Gap / Surplus:</span>
                              <span className={`font-bold text-2xl ${futureBusinessValueDisplay >= localRequiredSalePrice ? 'text-green-600' : 'text-red-600'}`}>
                                {futureBusinessValueDisplay >= localRequiredSalePrice ? '+' : ''}{formatCurrency(futureBusinessValueDisplay - localRequiredSalePrice)}
                              </span>
                            </div>
                            {futureBusinessValueDisplay >= localRequiredSalePrice ? (
                              <p className="text-green-700 font-semibold bg-green-50 p-2 rounded">
                                ✓ Your future business value exceeds the required amount! You're on track.
                              </p>
                            ) : (
                              <p className="text-red-700 font-semibold bg-red-50 p-2 rounded">
                                ⚠ Your future business value is below the required amount. Focus on increasing business value or adjusting lifestyle expectations.
                              </p>
                            )}
                          </>
                        ) : (
                          <div className="bg-green-50 border border-green-200 rounded p-3">
                            <p className="text-green-800 font-semibold">
                              ✓ Your personal net worth covers all lifestyle needs. This future business value of {formatCurrency(futureBusinessValueDisplay)} will provide additional wealth of {formatCurrency(desiredAfterTaxProceeds)} after taxes!
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 8: Sale Proceeds Structure - Lump Sum & Earnout */}
                  {futureBusinessValueDisplay > 0 && (wealthGap.lump_sum_percent > 0 || wealthGap.lump_sum_dollar > 0) && (
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-100 border-l-4 border-indigo-500 rounded-lg p-4">
                      <h5 className="font-bold text-indigo-900 mb-3 flex items-center gap-2">
                        <span className="bg-indigo-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs">8</span>
                        Sale Proceeds Structure (Lump Sum + Earnout)
                      </h5>
                      <div className="space-y-3 text-sm">
                        <p className="text-indigo-800 mb-2">
                          Your business sale is structured with a lump sum at closing and an earnout over time:
                        </p>

                        {/* Calculate breakdown */}
                        {(() => {
                          const ownershipDecimal = (wealthGap.ownership_percentage || 100) / 100;
                          const grossProceeds = futureBusinessValueDisplay * ownershipDecimal;

                          // Lump sum calculation
                          const lumpSumPercent = wealthGap.lump_sum_percent || 30;
                          const lumpSumDollar = wealthGap.lump_sum_dollar || 0;
                          const lumpSumGrossLocal = lumpSumDollar > 0 ? lumpSumDollar : (grossProceeds * lumpSumPercent / 100);
                          const lumpSumTax = lumpSumGrossLocal * lumpSumTaxRate;
                          const lumpSumAfterTax = lumpSumGrossLocal - lumpSumTax;

                          // Earnout calculation
                          const earnoutGrossLocal = grossProceeds - lumpSumGrossLocal;
                          const earnoutYears = wealthGap.earnout_years || 5;
                          const earnoutInterestRate = (wealthGap.earnout_interest_rate || 5) / 100;
                          const earnoutPaymentFrequency = wealthGap.earnout_payment_frequency || 'annual';

                          // Calculate annuity payment (gross)
                          const n = earnoutYears;
                          const earnoutAnnualPaymentGross = earnoutGrossLocal * (earnoutInterestRate * Math.pow(1 + earnoutInterestRate, n)) / (Math.pow(1 + earnoutInterestRate, n) - 1);
                          const earnoutMonthlyPaymentGross = earnoutAnnualPaymentGross / 12;

                          // Apply tax to each payment
                          const earnoutAnnualPaymentAfterTax = earnoutAnnualPaymentGross * (1 - earnoutTaxRate);
                          const earnoutMonthlyPaymentAfterTax = earnoutMonthlyPaymentGross * (1 - earnoutTaxRate);

                          const earnoutTotalTax = earnoutGrossLocal * earnoutTaxRate;
                          const earnoutAfterTax = earnoutGrossLocal - earnoutTotalTax;

                          return (
                            <>
                              {/* Step 1: Gross Proceeds */}
                              <div className="bg-white/70 rounded p-3 border border-indigo-200">
                                <p className="text-indigo-900 font-semibold mb-2">Step 1: Calculate Your Share</p>
                                <div className="space-y-1 ml-4 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-indigo-700">Future Business Value:</span>
                                    <span className="font-semibold">{formatCurrency(futureBusinessValueDisplay)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-indigo-700">× Your Ownership:</span>
                                    <span className="font-semibold">{wealthGap.ownership_percentage}%</span>
                                  </div>
                                  <div className="border-t border-indigo-300 pt-1 flex justify-between">
                                    <span className="text-indigo-900 font-bold">= Your Gross Proceeds:</span>
                                    <span className="font-bold text-indigo-900">{formatCurrency(grossProceeds)}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Step 2: Lump Sum */}
                              <div className="bg-blue-50 rounded p-3 border-2 border-blue-300">
                                <p className="text-blue-900 font-semibold mb-2">Step 2: Lump Sum at Closing</p>
                                <div className="space-y-1 ml-4 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-blue-700">Gross Proceeds:</span>
                                    <span className="font-semibold">{formatCurrency(grossProceeds)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-blue-700">× Lump Sum Amount (% or $):</span>
                                    <span className="font-semibold">
                                      {lumpSumDollar > 0 ? formatCurrency(lumpSumDollar) : `${lumpSumPercent}%`}
                                    </span>
                                  </div>
                                  <div className="border-t border-blue-300 pt-1 flex justify-between">
                                    <span className="text-blue-900 font-bold">= Lump Sum (Gross):</span>
                                    <span className="font-bold text-blue-900">{formatCurrency(lumpSumGrossLocal)}</span>
                                  </div>
                                  <div className="flex justify-between mt-2 pt-2 border-t border-blue-200">
                                    <span className="text-blue-700">Effective Tax Rate:</span>
                                    <span className="font-semibold">{(lumpSumTaxRate * 100).toFixed(0)}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-blue-700">Tax Amount:</span>
                                    <span className="font-semibold text-red-600">-{formatCurrency(lumpSumTax)}</span>
                                  </div>
                                  <div className="border-t-2 border-blue-400 pt-1 flex justify-between">
                                    <span className="text-blue-900 font-bold">Lump Sum After Tax:</span>
                                    <span className="font-bold text-lg text-green-700">{formatCurrency(lumpSumAfterTax)}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Step 3: Earnout */}
                              <div className="bg-purple-50 rounded p-3 border-2 border-purple-300">
                                <p className="text-purple-900 font-semibold mb-2">Step 3: Earnout Structure</p>
                                <div className="space-y-1 ml-4 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-purple-700">Gross Proceeds:</span>
                                    <span className="font-semibold">{formatCurrency(grossProceeds)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-purple-700">- Lump Sum:</span>
                                    <span className="font-semibold text-blue-700">-{formatCurrency(lumpSumGrossLocal)}</span>
                                  </div>
                                  <div className="border-t border-purple-300 pt-1 flex justify-between">
                                    <span className="text-purple-900 font-bold">= Earnout (Gross):</span>
                                    <span className="font-bold text-purple-900">{formatCurrency(earnoutGrossLocal)}</span>
                                  </div>

                                  <div className="mt-3 pt-2 border-t border-purple-200 space-y-1">
                                    <p className="text-purple-800 font-semibold mb-1">Payment Schedule:</p>
                                    <div className="flex justify-between">
                                      <span className="text-purple-700">Duration:</span>
                                      <span className="font-semibold">{earnoutYears} years</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-purple-700">Interest Rate:</span>
                                      <span className="font-semibold">{(earnoutInterestRate * 100).toFixed(1)}%</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-purple-700">Frequency:</span>
                                      <span className="font-semibold capitalize">{earnoutPaymentFrequency}</span>
                                    </div>
                                  </div>

                                  <div className="mt-2 pt-2 border-t border-purple-200 space-y-1">
                                    <p className="text-purple-800 font-semibold mb-1">
                                      {earnoutPaymentFrequency === 'monthly' ? 'Monthly' : 'Annual'} Payment:
                                    </p>
                                    <div className="flex justify-between">
                                      <span className="text-purple-700">Gross Payment:</span>
                                      <span className="font-semibold">
                                        {formatCurrency(earnoutPaymentFrequency === 'monthly' ? earnoutMonthlyPaymentGross : earnoutAnnualPaymentGross)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-purple-700">Effective Tax Rate:</span>
                                      <span className="font-semibold">{(earnoutTaxRate * 100).toFixed(0)}%</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-purple-700">Tax per Payment:</span>
                                      <span className="font-semibold text-red-600">
                                        -{formatCurrency((earnoutPaymentFrequency === 'monthly' ? earnoutMonthlyPaymentGross : earnoutAnnualPaymentGross) * earnoutTaxRate)}
                                      </span>
                                    </div>
                                    <div className="border-t-2 border-purple-400 pt-1 flex justify-between">
                                      <span className="text-purple-900 font-bold">After-Tax Payment:</span>
                                      <span className="font-bold text-lg text-green-700">
                                        {formatCurrency(earnoutPaymentFrequency === 'monthly' ? earnoutMonthlyPaymentAfterTax : earnoutAnnualPaymentAfterTax)}
                                      </span>
                                    </div>
                                  </div>

                                  <p className="text-xs text-purple-600 mt-2 italic bg-purple-100 rounded p-2">
                                    💡 Tax applied to each {earnoutPaymentFrequency} payment as received (ordinary income), not upfront
                                  </p>
                                </div>
                              </div>

                              {/* Step 4: Total After-Tax */}
                              <div className="bg-gradient-to-r from-green-100 to-emerald-200 rounded p-3 border-2 border-green-500">
                                <p className="text-green-900 font-semibold mb-2">Step 4: Total After-Tax Value</p>
                                <div className="space-y-1 ml-4 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-green-800">Lump Sum After Tax:</span>
                                    <span className="font-semibold">{formatCurrency(lumpSumAfterTax)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-green-800">Earnout After Tax:</span>
                                    <span className="font-semibold">{formatCurrency(earnoutAfterTax)}</span>
                                  </div>
                                  <div className="border-t-2 border-green-600 pt-2 flex justify-between items-center">
                                    <span className="text-green-900 font-bold text-base">Total After-Tax Proceeds:</span>
                                    <span className="font-bold text-2xl text-green-700">
                                      {formatCurrency(lumpSumAfterTax + earnoutAfterTax)}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="bg-yellow-50 rounded p-3 border border-yellow-300">
                                <p className="text-yellow-900 font-semibold text-xs mb-1">
                                  🔑 Key Tax Difference:
                                </p>
                                <p className="text-xs text-yellow-800">
                                  <strong>Lump Sum ({(lumpSumTaxRate * 100).toFixed(0)}%):</strong> Typically taxed as capital gains at closing.
                                  <br />
                                  <strong>Earnout ({(earnoutTaxRate * 100).toFixed(0)}%):</strong> Typically taxed as ordinary income on each payment received over {earnoutYears} years.
                                </p>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Step 9: Post-Exit Asset Growth - Phase 2 */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-100 border-l-4 border-indigo-500 rounded-lg p-4">
                    <h5 className="font-bold text-indigo-900 mb-3 flex items-center gap-2">
                      <span className="bg-indigo-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs">9</span>
                      Post-Exit Asset Growth - Phase 2 (Retirement)
                    </h5>
                    <div className="space-y-3 text-sm">
                      <p className="text-indigo-800 mb-2">
                        Your assets grow from exit until life expectancy ({yearsPostExit} years) at post-exit return rates:
                      </p>

                      {/* Liquid Assets */}
                      <div className="bg-white/70 rounded p-3 border border-indigo-200">
                        <p className="text-indigo-900 font-semibold mb-2">Liquid Assets:</p>
                        <div className="space-y-1 ml-4 text-xs">
                          <div className="flex justify-between">
                            <span className="text-indigo-700">Balance at exit (before lump sum):</span>
                            <span className="font-semibold">{formatCurrency(liquidAssetsFV)}</span>
                          </div>
                          {lumpSumToLiquid > 0 && (
                            <div className="flex justify-between">
                              <span className="text-indigo-700">+ Lump sum allocation ({lumpSumAllocLiquid}%):</span>
                              <span className="font-semibold text-blue-700">+{formatCurrency(lumpSumToLiquid)}</span>
                            </div>
                          )}
                          {lumpSumToLiquid > 0 && (
                            <div className="flex justify-between">
                              <span className="text-indigo-700 font-semibold">Total balance at exit:</span>
                              <span className="font-semibold text-blue-900">{formatCurrency(liquidAssetsFV + lumpSumToLiquid)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-indigo-700">Post-exit return rate:</span>
                            <span className="font-semibold">{postExitLiquidAssetsRate}%</span>
                          </div>

                          {/* Show earnout contribution if there is one */}
                          {monthlyEarnoutPaymentLiquid > 0 && yearsWithEarnout > 0 && (
                            <div className="bg-green-50 border border-green-300 rounded p-2 my-2">
                              <div className="flex justify-between mb-1">
                                <span className="text-green-800 font-semibold">Earnout allocation ({earnoutAllocLiquid}%) ({earnoutPaymentFrequency}):</span>
                                <span className="font-semibold text-green-700">
                                  +{formatCurrency(earnoutPaymentFrequency === 'monthly' ? monthlyEarnoutPaymentLiquid : monthlyEarnoutPaymentLiquid * 12)}
                                  {earnoutPaymentFrequency === 'monthly' ? '/mo' : '/yr'}
                                </span>
                              </div>
                              <div className="flex justify-between text-xxs">
                                <span className="text-green-700">Earnout period:</span>
                                <span className="font-semibold">{yearsWithEarnout} years</span>
                              </div>
                            </div>
                          )}

                          {postExitMonthlyWithdrawal > 0 && postExitSourceCategory === 'liquid_assets' && (
                            <div className="flex justify-between">
                              <span className="text-indigo-700">Monthly withdrawal growing with inflation (post-exit):</span>
                              <span className="font-semibold text-purple-700">-{formatCurrency(postExitMonthlyWithdrawal)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-indigo-700">Growth period:</span>
                            <span className="font-semibold">{yearsPostExit} years</span>
                          </div>
                          <div className="border-t border-indigo-300 pt-1 flex justify-between">
                            <span className="text-indigo-900 font-bold">Value at age {lifeExp}:</span>
                            <span className="font-bold text-green-700">{formatCurrency(liquidAssetsFVEndOfLife)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Retirement Accounts */}
                      <div className="bg-white/70 rounded p-3 border border-indigo-200">
                        <p className="text-indigo-900 font-semibold mb-2">Retirement Accounts:</p>
                        <div className="space-y-1 ml-4 text-xs">
                          <div className="flex justify-between">
                            <span className="text-indigo-700">Balance at exit:</span>
                            <span className="font-semibold">{formatCurrency(retirementAccountsFV)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-indigo-700">Post-exit return rate:</span>
                            <span className="font-semibold">{postExitRetirementAccountsRate}%</span>
                          </div>
                          {postExitMonthlyWithdrawal > 0 && postExitSourceCategory === 'retirement_accounts' && (
                            <div className="flex justify-between">
                              <span className="text-indigo-700">Monthly withdrawal growing with inflation (post-exit):</span>
                              <span className="font-semibold text-purple-700">-{formatCurrency(postExitMonthlyWithdrawal)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-indigo-700">Growth period:</span>
                            <span className="font-semibold">{yearsPostExit} years</span>
                          </div>
                          <div className="border-t border-indigo-300 pt-1 flex justify-between">
                            <span className="text-indigo-900 font-bold">Value at age {lifeExp}:</span>
                            <span className="font-bold text-green-700">{formatCurrency(retirementAccountsFVEndOfLife)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Real Estate */}
                      <div className="bg-white/70 rounded p-3 border border-indigo-200">
                        <p className="text-indigo-900 font-semibold mb-2">Real Estate Equity:</p>
                        <div className="space-y-1 ml-4 text-xs">
                          <div className="flex justify-between">
                            <span className="text-indigo-700">Equity at exit (before lump sum):</span>
                            <span className="font-semibold">{formatCurrency(realEstateFV)}</span>
                          </div>
                          {lumpSumToRealEstate > 0 && (
                            <div className="flex justify-between">
                              <span className="text-indigo-700">+ Lump sum allocation ({lumpSumAllocRealEstate}%):</span>
                              <span className="font-semibold text-blue-700">+{formatCurrency(lumpSumToRealEstate)}</span>
                            </div>
                          )}
                          {lumpSumToRealEstate > 0 && (
                            <div className="flex justify-between">
                              <span className="text-indigo-700 font-semibold">Total equity at exit:</span>
                              <span className="font-semibold text-blue-900">{formatCurrency(realEstateFV + lumpSumToRealEstate)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-indigo-700">Post-exit appreciation rate:</span>
                            <span className="font-semibold">{postExitRealEstateRate}%</span>
                          </div>

                          {/* Show earnout contribution if there is one */}
                          {monthlyEarnoutPaymentRealEstate > 0 && yearsWithEarnout > 0 && (
                            <div className="bg-green-50 border border-green-300 rounded p-2 my-2">
                              <div className="flex justify-between mb-1">
                                <span className="text-green-800 font-semibold">Earnout allocation ({earnoutAllocRealEstate}%) ({earnoutPaymentFrequency}):</span>
                                <span className="font-semibold text-green-700">
                                  +{formatCurrency(earnoutPaymentFrequency === 'monthly' ? monthlyEarnoutPaymentRealEstate : monthlyEarnoutPaymentRealEstate * 12)}
                                  {earnoutPaymentFrequency === 'monthly' ? '/mo' : '/yr'}
                                </span>
                              </div>
                              <div className="flex justify-between text-xxs">
                                <span className="text-green-700">Earnout period:</span>
                                <span className="font-semibold">{yearsWithEarnout} years</span>
                              </div>
                            </div>
                          )}

                          {postExitMonthlyWithdrawal > 0 && (postExitSourceCategory === 'real_estate' || postExitSourceCategory === 'real_estate_equity') && (
                            <div className="flex justify-between">
                              <span className="text-indigo-700">Monthly withdrawal growing with inflation (post-exit):</span>
                              <span className="font-semibold text-purple-700">-{formatCurrency(postExitMonthlyWithdrawal)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-indigo-700">Growth period:</span>
                            <span className="font-semibold">{yearsPostExit} years</span>
                          </div>
                          <div className="border-t border-indigo-300 pt-1 flex justify-between">
                            <span className="text-indigo-900 font-bold">Value at age {lifeExp}:</span>
                            <span className="font-bold text-green-700">{formatCurrency(realEstateFVEndOfLife)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Other Investments */}
                      <div className="bg-white/70 rounded p-3 border border-indigo-200">
                        <p className="text-indigo-900 font-semibold mb-2">Other Investments:</p>
                        <div className="space-y-1 ml-4 text-xs">
                          <div className="flex justify-between">
                            <span className="text-indigo-700">Balance at exit (before lump sum):</span>
                            <span className="font-semibold">{formatCurrency(otherInvestmentsBaseFV)}</span>
                          </div>
                          {lumpSumToOtherInv > 0 && (
                            <div className="flex justify-between">
                              <span className="text-indigo-700">+ Lump sum allocation ({lumpSumAllocOtherInv}%):</span>
                              <span className="font-semibold text-blue-700">+{formatCurrency(lumpSumToOtherInv)}</span>
                            </div>
                          )}
                          {lumpSumToOtherInv > 0 && (
                            <div className="flex justify-between">
                              <span className="text-indigo-700 font-semibold">Total balance at exit:</span>
                              <span className="font-semibold text-blue-900">{formatCurrency(otherInvestmentsFV + lumpSumToOtherInv)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-indigo-700">Post-exit return rate:</span>
                            <span className="font-semibold">{postExitOtherInvestmentsRate}%</span>
                          </div>

                          {/* Show earnout contribution if there is one */}
                          {monthlyEarnoutPaymentOtherInv > 0 && yearsWithEarnout > 0 && (
                            <div className="bg-green-50 border border-green-300 rounded p-2 my-2">
                              <div className="flex justify-between mb-1">
                                <span className="text-green-800 font-semibold">Earnout allocation ({earnoutAllocOtherInv}%) ({earnoutPaymentFrequency}):</span>
                                <span className="font-semibold text-green-700">
                                  +{formatCurrency(earnoutPaymentFrequency === 'monthly' ? monthlyEarnoutPaymentOtherInv : monthlyEarnoutPaymentOtherInv * 12)}
                                  {earnoutPaymentFrequency === 'monthly' ? '/mo' : '/yr'}
                                </span>
                              </div>
                              <div className="flex justify-between text-xxs">
                                <span className="text-green-700">Earnout period:</span>
                                <span className="font-semibold">{yearsWithEarnout} years</span>
                              </div>
                            </div>
                          )}

                          {postExitMonthlyWithdrawal > 0 && postExitSourceCategory === 'other_investments' && (
                            <div className="flex justify-between">
                              <span className="text-indigo-700">Monthly withdrawal growing with inflation (post-exit):</span>
                              <span className="font-semibold text-purple-700">-{formatCurrency(postExitMonthlyWithdrawal)}</span>
                            </div>
                          )}

                          {/* Show two-phase growth if earnout period < total post-exit period */}
                          {yearsAfterEarnout > 0 ? (
                            <>
                              <div className="flex justify-between">
                                <span className="text-indigo-700">Phase 1 (with earnout):</span>
                                <span className="font-semibold">{yearsWithEarnout} years</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-indigo-700">Value after earnout period:</span>
                                <span className="font-semibold text-blue-700">{formatCurrency(otherInvestmentsFVAfterEarnout)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-indigo-700">Phase 2 (after earnout):</span>
                                <span className="font-semibold">{yearsAfterEarnout} years</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-between">
                              <span className="text-indigo-700">Growth period (with earnout):</span>
                              <span className="font-semibold">{yearsPostExit} years</span>
                            </div>
                          )}

                          <div className="border-t border-indigo-300 pt-1 flex justify-between">
                            <span className="text-indigo-900 font-bold">Value at age {lifeExp}:</span>
                            <span className="font-bold text-green-700">{formatCurrency(otherInvestmentsFVEndOfLife)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Liabilities */}
                      {(wealthGap.total_liabilities || 0) > 0 && (
                        <div className="bg-red-50 rounded p-3 border border-red-200">
                          <p className="text-red-900 font-semibold mb-2">Total Liabilities:</p>
                          <div className="space-y-1 ml-4 text-xs">
                            <div className="flex justify-between">
                              <span className="text-red-700">Balance at exit:</span>
                              <span className="font-semibold">{formatCurrency(futureLiabilities)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-red-700">Post-exit reduction rate:</span>
                              <span className="font-semibold">{postExitLiabilityRate}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-red-700">Paydown period:</span>
                              <span className="font-semibold">{yearsPostExit} years</span>
                            </div>
                            <div className="border-t border-red-300 pt-1 flex justify-between">
                              <span className="text-red-900 font-bold">Balance at age {lifeExp}:</span>
                              <span className="font-bold text-red-700">{formatCurrency(liabilitiesFVEndOfLife)}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Total Post-Exit Growth */}
                      <div className="bg-gradient-to-r from-purple-100 to-indigo-200 rounded p-3 border-2 border-indigo-500">
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-indigo-900 font-bold">Total Assets at Age {lifeExp}:</span>
                            <span className="font-bold text-xl text-indigo-700">
                              {formatCurrency(liquidAssetsFVEndOfLife + retirementAccountsFVEndOfLife + realEstateFVEndOfLife + otherInvestmentsFVEndOfLife)}
                            </span>
                          </div>
                          {(wealthGap.total_liabilities || 0) > 0 && (
                            <>
                              <div className="flex justify-between items-center">
                                <span className="text-red-800">Less Liabilities:</span>
                                <span className="font-semibold text-red-700">-{formatCurrency(liabilitiesFVEndOfLife)}</span>
                              </div>
                              <div className="border-t-2 border-indigo-600 pt-1 flex justify-between items-center">
                                <span className="text-indigo-900 font-bold">Net Worth at Age {lifeExp}:</span>
                                <span className="font-bold text-2xl text-indigo-700">{formatCurrency(netWorthAtEndOfLife)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  

                  

                  
                </div>
              )}
            </div>
          )}

          

          {/* Projected Position at Exit */}
          {hasRequiredData && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="font-bold text-lg text-gray-900 mb-4">Financial Summary at Exit ({wealthGap.exit_year})</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <p className="text-xs text-emerald-700 mb-2">Personal Net Worth Growth</p>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-emerald-800">Current:</span>
                    <span className="font-semibold text-emerald-900">{formatCurrency(wealthGap.current_net_worth)}</span>
                  </div>
                  <div className="flex justify-between items-baseline mt-1">
                    <span className="text-sm text-emerald-800">At {yearsUntilExit} years @ {requiredData.weighted_return_rate?.toFixed(2)}%:</span>
                    <span className="font-bold text-lg text-emerald-700">{formatCurrency(futureNetWorth)}</span>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-xs text-blue-700 mb-2">Total Capital Needed</p>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-blue-800">To sustain lifestyle:</span>
                    <span className="font-bold text-lg text-blue-700">{formatCurrency(requiredData.total_capital_needed)}</span>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">Present value of all future spending</p>
                </div>

                <div className={`rounded-lg p-4 border-2 ${localWealthSurplus > 0 ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                  <p className={`text-xs mb-2 ${localWealthSurplus > 0 ? 'text-green-700' : 'text-orange-700'}`}>
                    {localWealthSurplus > 0 ? 'Wealth Surplus' : 'Gap from Business'}
                  </p>
                  <div className="flex justify-between items-baseline">
                    <span className={`text-sm ${localWealthSurplus > 0 ? 'text-green-800' : 'text-orange-800'}`}>
                      {localWealthSurplus > 0 ? 'Surplus:' : 'Needed from sale:'}
                    </span>
                    <span className={`font-bold text-2xl ${localWealthSurplus > 0 ? 'text-green-700' : 'text-orange-700'}`}>
                      {formatCurrency(localWealthSurplus > 0 ? localWealthSurplus : localNetGapFromBusiness)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* What This Means */}
          {wealthGap.wealth_goal_method === 'monthly_needs' && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="text-blue-600" size={20} />
              What This Means For You
            </h4>
            <div className="text-sm text-gray-700 space-y-4">
              {hasRequiredData ? (
                <>
                  {/* Lifestyle Needs Overview */}
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h5 className="font-semibold text-blue-900 mb-2">Your Lifestyle Funding Needs</h5>
                    <p className="mb-2">
                      To maintain your desired lifestyle from exit through age {wealthGap.life_expectancy} ({wealthGap.life_expectancy - ageAtExit} years), you need a total of <strong className="text-blue-700">{formatCurrency(requiredData.total_capital_needed)}</strong> in capital at your exit date.
                    </p>
                    <div className="text-xs text-gray-600 ml-4 space-y-1">
                      <p>• Current monthly need: <strong>{formatCurrency(wealthGap.monthly_cash_need)}</strong></p>
                      <p>• Less outside income: <strong>-{formatCurrency(wealthGap.outside_income_monthly || 0)}</strong></p>
                      <p>• Current net monthly need: <strong className="text-blue-600">{formatCurrency(Math.max(0, wealthGap.monthly_cash_need - (wealthGap.outside_income_monthly || 0)))}</strong></p>
                      <p>• Projected to exit year {wealthGap.exit_year}: <strong className="text-blue-700">{formatCurrency(netMonthlyNeed)}/month</strong></p>
                      <p>• This accounts for {wealthGap.annual_inflation_rate}% inflation and {wealthGap.outside_income_annual_increase}% annual growth in outside income</p>
                    </div>
                  </div>

                  {/* Personal Net Worth Factor */}
                  <div className={`rounded-lg p-4 border-2 ${localWealthSurplus > 0 ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                    <h5 className={`font-semibold mb-2 ${localWealthSurplus > 0 ? 'text-green-900' : 'text-orange-900'}`}>
                      Your Personal Net Worth Impact
                    </h5>
                    <p className="mb-2">
                      Your current personal net worth of <strong>{formatCurrency(wealthGap.current_net_worth)}</strong> (excluding business value) may grow to approximately <strong className={localWealthSurplus > 0 ? 'text-green-700' : 'text-orange-700'}>{formatCurrency(futureNetWorth)}</strong> by {wealthGap.exit_year}, assuming a weighted average return of <strong>{requiredData.weighted_return_rate?.toFixed(2)}%</strong> annually.
                    </p>
                    {localWealthSurplus > 0 ? (
                      <>
                        <p className="font-semibold text-green-800 mb-2">
                          🎉 Excellent Financial Position!
                        </p>
                        <p className="mb-2">
                          Your personal assets may grow to <strong className="text-green-700">{formatCurrency(localWealthSurplus)}</strong> MORE than needed to sustain your lifestyle. This means:
                        </p>
                        <ul className="text-xs ml-4 space-y-1 text-green-800">
                          <li>✓ You may be able to exit your business <strong>at any time</strong> without financial stress</li>
                          <li>✓ Any business sale proceeds may be <strong>additional wealth</strong> beyond your needs</li>
                          <li>✓ You may have flexibility to pursue passion projects or philanthropy</li>
                          <li>✓ Strong legacy potential for heirs or charitable giving</li>
                        </ul>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold text-orange-800 mb-2">
                          Business Value Required
                        </p>
                        <p className="mb-2">
                          After your personal net worth growth, your business may need to provide <strong className="text-orange-700">{formatCurrency(localNetGapFromBusiness)}</strong> in after-tax proceeds to cover the remaining gap.
                        </p>
                        <div className="text-xs ml-4 space-y-1">
                          <p>• Total capital needed: <strong>{formatCurrency(requiredData.total_capital_needed)}</strong></p>
                          <p>• Personal net worth provides: <strong className="text-green-700">-{formatCurrency(futureNetWorth)}</strong></p>
                          <p>• Gap from business sale: <strong className="text-orange-700">{formatCurrency(localNetGapFromBusiness)}</strong></p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Business Sale Requirements */}
                  {localNetGapFromBusiness > 0 ? (
                    <div className="bg-white rounded-lg p-4 border border-orange-200">
                      <h5 className="font-semibold text-orange-900 mb-2">Required Business Sale Price</h5>
                      <p className="mb-3">
                        To net <strong>{formatCurrency(localNetGapFromBusiness)}</strong> after taxes from your business sale, with <strong>{wealthGap.ownership_percentage}%</strong> ownership, your business may need to sell for approximately:
                      </p>
                      <div className="bg-orange-50 rounded p-3 mb-3 border border-orange-200">
                        <p className="text-2xl font-bold text-orange-700 text-center">
                          {formatCurrency(localRequiredSalePrice)}
                        </p>
                        <p className="text-xs text-center text-orange-600 mt-1">Pre-tax business valuation needed</p>
                      </div>

                      {/* Tax Calculation with Lump Sum & Earnout */}
                      {(wealthGap.lump_sum_percent > 0 || wealthGap.lump_sum_dollar > 0) ? (
                        <div className="text-xs text-gray-700 space-y-3">
                          <p className="font-semibold text-gray-900">Sale Structure & Tax Treatment:</p>

                          {/* Calculate breakdown */}
                          {(() => {
                            const ownershipDecimal = (wealthGap.ownership_percentage || 100) / 100;
                            const grossProceeds = localRequiredSalePrice * ownershipDecimal;

                            const lumpSumPercent = wealthGap.lump_sum_percent || 30;
                            const lumpSumDollar = wealthGap.lump_sum_dollar || 0;
                            const lumpSumGrossLocal2 = lumpSumDollar > 0 ? lumpSumDollar : (grossProceeds * lumpSumPercent / 100);
                            const lumpSumTax = lumpSumGrossLocal2 * lumpSumTaxRate;
                            const lumpSumAfterTax = lumpSumGrossLocal2 - lumpSumTax;

                            const earnoutGrossLocal2 = grossProceeds - lumpSumGrossLocal2;
                            const earnoutTax = earnoutGrossLocal2 * earnoutTaxRate;
                            const earnoutAfterTax = earnoutGrossLocal2 - earnoutTax;

                            const totalTax = lumpSumTax + earnoutTax;
                            const totalAfterTax = lumpSumAfterTax + earnoutAfterTax;

                            return (
                              <>
                                <div className="ml-4 space-y-2 bg-gray-50 rounded p-3">
                                  {/* Gross Proceeds */}
                                  <div className="pb-2 border-b border-gray-300">
                                    <div className="flex justify-between items-center">
                                      <span>Sale price:</span>
                                      <span className="font-semibold">{formatCurrency(localRequiredSalePrice)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span>Your ownership ({wealthGap.ownership_percentage}%):</span>
                                      <span className="font-semibold">{formatCurrency(grossProceeds)}</span>
                                    </div>
                                  </div>

                                  {/* Lump Sum */}
                                  <div className="bg-blue-50 rounded p-2 border border-blue-200">
                                    <p className="font-semibold text-blue-900 mb-1">Lump Sum at Closing:</p>
                                    <div className="flex justify-between items-center">
                                      <span className="text-blue-700">Amount:</span>
                                      <span className="font-semibold">{formatCurrency(lumpSumGross)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span className="text-blue-700">Tax ({(lumpSumTaxRate * 100).toFixed(0)}%):</span>
                                      <span className="font-semibold text-red-600">-{formatCurrency(lumpSumTax)}</span>
                                    </div>
                                    <div className="flex justify-between items-center pt-1 border-t border-blue-300">
                                      <span className="font-bold text-blue-900">After tax:</span>
                                      <span className="font-bold text-green-700">{formatCurrency(lumpSumAfterTax)}</span>
                                    </div>
                                  </div>

                                  {/* Earnout */}
                                  <div className="bg-purple-50 rounded p-2 border border-purple-200">
                                    <p className="font-semibold text-purple-900 mb-1">Earnout (paid over {wealthGap.earnout_years || 5} years):</p>
                                    <div className="flex justify-between items-center">
                                      <span className="text-purple-700">Amount:</span>
                                      <span className="font-semibold">{formatCurrency(earnoutGross)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span className="text-purple-700">Tax ({(earnoutTaxRate * 100).toFixed(0)}%):</span>
                                      <span className="font-semibold text-red-600">-{formatCurrency(earnoutTax)}</span>
                                    </div>
                                    <div className="flex justify-between items-center pt-1 border-t border-purple-300">
                                      <span className="font-bold text-purple-900">After tax:</span>
                                      <span className="font-bold text-green-700">{formatCurrency(earnoutAfterTax)}</span>
                                    </div>
                                    <p className="text-xs text-purple-600 mt-1 italic">
                                      *Tax applied to each {wealthGap.earnout_payment_frequency || 'annual'} payment as received
                                    </p>
                                  </div>

                                  {/* Total */}
                                  <div className="pt-2 border-t-2 border-gray-400">
                                    <div className="flex justify-between items-center">
                                      <span className="text-red-800">Total taxes:</span>
                                      <span className="font-bold text-red-600">-{formatCurrency(totalTax)}</span>
                                    </div>
                                    <div className="flex justify-between items-center mt-1 pt-1 border-t border-gray-300">
                                      <span className="font-bold text-gray-900">Total after-tax proceeds:</span>
                                      <span className="font-bold text-green-700">{formatCurrency(totalAfterTax)}</span>
                                    </div>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-600 space-y-1">
                          <p><strong>Tax Calculation:</strong></p>
                          <p className="ml-4">• Sale price: <strong>{formatCurrency(localRequiredSalePrice)}</strong></p>
                          <p className="ml-4">• Your ownership ({wealthGap.ownership_percentage}%): <strong>{formatCurrency(localRequiredSalePrice * (wealthGap.ownership_percentage / 100))}</strong></p>
                          <p className="ml-4">• Less taxes ({wealthGap.tax_rate_on_sale}%): <strong className="text-red-600">-{formatCurrency(localRequiredSalePrice * (wealthGap.ownership_percentage / 100) * (wealthGap.tax_rate_on_sale / 100))}</strong></p>
                          <p className="ml-4 border-t border-gray-300 pt-1">• After-tax proceeds: <strong className="text-green-700">{formatCurrency(afterTaxProceeds)}</strong></p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-green-50 rounded-lg p-4 border border-green-300">
                      <h5 className="font-semibold text-green-900 mb-2">💰 No Business Sale Required</h5>
                      <p className="text-green-800">
                        Since your personal net worth may cover all lifestyle needs, any proceeds from selling your business may be <strong>pure upside</strong>. Consider:
                      </p>
                      <ul className="text-xs mt-2 ml-4 space-y-1 text-green-800">
                        <li>• Enhancing your lifestyle or pursuing luxury goals</li>
                        <li>• Creating a substantial legacy for heirs</li>
                        <li>• Funding charitable causes or foundations</li>
                        <li>• Making strategic investments or acquisitions</li>
                        <li>• Taking more time to find the perfect buyer at the best price</li>
                      </ul>
                    </div>
                  )}

                  {/* Future Business Value Analysis */}
                  {futureBusinessValueDisplay > 0 && (() => {
                    // Calculate after-tax proceeds for display
                    const ownershipPct = (wealthGap.ownership_percentage || 100) / 100;
                    const grossProceeds = futureBusinessValueDisplay * ownershipPct;
                    const lumpSumPct = (wealthGap.lump_sum_percent || 30) / 100;
                    const lumpSumDollar = wealthGap.lump_sum_dollar || 0;
                    const lumpSumGrossLocal3 = lumpSumDollar > 0 ? lumpSumDollar : (grossProceeds * lumpSumPct);
                    const lumpSumAfterTax = lumpSumGrossLocal3 * (1 - lumpSumTaxRate);

                    const earnoutGrossLocal3 = grossProceeds - lumpSumGrossLocal3;
                    const earnoutAfterTax = earnoutGrossLocal3 * (1 - earnoutTaxRate);

                    const totalAfterTaxProceeds = lumpSumAfterTax + earnoutAfterTax;

                    return (
                      <div className="bg-white rounded-lg p-4 border border-gray-300">
                        <h5 className="font-semibold text-gray-900 mb-2">Future Business Value at Exit</h5>
                        <p className="mb-2">
                          Your business's projected value at exit is <strong className="text-blue-700">{formatCurrency(futureBusinessValueDisplay)}</strong>, which may provide <strong className="text-green-700">{formatCurrency(totalAfterTaxProceeds)}</strong> in after-tax proceeds.
                        </p>
                      {localWealthSurplus > 0 ? (
                        <p className="text-green-700 text-sm">
                          ✓ This may add significantly to your already comfortable financial position! You may be in an excellent position to exit whenever you choose.
                        </p>
                      ) : localNetGapFromBusiness > 0 ? (
                        <>
                          {futureBusinessValueDisplay >= localRequiredSalePrice ? (
                            <>
                              <p className="text-green-700 font-semibold mb-2">
                                ✓ Your future business value may EXCEED the required amount!
                              </p>
                              <p className="text-sm text-gray-700">
                                Your business's projected value is <strong>{formatCurrency(futureBusinessValueDisplay - localRequiredSalePrice)}</strong> above what you may need for your lifestyle goals. This may provide a strong financial cushion and exit flexibility.
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="text-orange-700 font-semibold mb-2">
                                ⚠ Value Gap: {formatCurrency(localRequiredSalePrice - futureBusinessValueDisplay)} needed
                              </p>
                              <p className="text-sm text-gray-700 mb-2">
                                Your business may need to increase in value by <strong className="text-orange-700">{((localRequiredSalePrice / futureBusinessValueDisplay - 1) * 100).toFixed(1)}%</strong> to meet your financial goals. Consider:
                              </p>
                              <ul className="text-xs ml-4 space-y-1 text-gray-600">
                                <li>• Implementing value-building strategies from your assessment</li>
                                <li>• Extending your exit timeline to allow more growth</li>
                                <li>• Increasing business profitability and recurring revenue</li>
                                <li>• Reducing owner dependency to increase marketability</li>
                                <li>• Building stronger systems and management team</li>
                              </ul>
                            </>
                          )}
                        </>
                      ) : null}
                      </div>
                    );
                  })()}

                  {/* Timeline and Next Steps */}
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
                    <h5 className="font-semibold text-purple-900 mb-2">Timeline & Action Items</h5>
                    <div className="text-sm space-y-2">
                      <p>
                        <strong>Time to Exit:</strong> {yearsUntilExit} years ({currentYear} → {wealthGap.exit_year})
                      </p>
                      <p className="text-xs text-gray-600">
                        You'll be age {ageAtExit} at exit, with {wealthGap.life_expectancy - ageAtExit} years to fund through age {wealthGap.life_expectancy}.
                      </p>
                      <div className="mt-3 pt-3 border-t border-purple-200">
                        <p className="font-semibold text-purple-900 mb-2">Recommended Next Steps:</p>
                        <ul className="text-xs space-y-1 ml-4 text-gray-700">
                          {localWealthSurplus > 0 ? (
                            <>
                              <li>✓ Review your exit planning checklist to optimize timing</li>
                              <li>✓ Consider tax strategies to maximize after-tax proceeds</li>
                              <li>✓ Explore estate planning to preserve wealth for heirs</li>
                              <li>✓ Evaluate whether to hold out for premium offers</li>
                            </>
                          ) : futureBusinessValueDisplay >= localRequiredSalePrice ? (
                            <>
                              <li>✓ Continue building value to create a larger cushion</li>
                              <li>✓ Focus on the highest-impact value drivers</li>
                              <li>✓ Begin exit planning and process preparation</li>
                              <li>✓ Consider timing the market for optimal sale conditions</li>
                            </>
                          ) : (
                            <>
                              <li>⚠ Prioritize tasks that directly increase business value</li>
                              <li>⚠ Address critical gaps in your readiness assessment</li>
                              <li>⚠ Consider extending timeline or adjusting lifestyle needs</li>
                              <li>⚠ Focus on profit improvement and revenue growth</li>
                              <li>⚠ Build management systems to increase marketability</li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <p>
                  {wealthGap.calculated_wealth_gap > 0
                    ? `You need ${formatCurrency(wealthGap.calculated_wealth_gap)} from your business exit to reach your wealth goal of ${formatCurrency(wealthGap.calculated_wealth_goal)} and maintain your desired lifestyle.`
                    : `Congratulations! You have a surplus of ${formatCurrency(Math.abs(wealthGap.calculated_wealth_gap))}. Your current net worth already exceeds your wealth goal.`
                  }
                </p>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

WealthGapDetailsModalEnhanced.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  wealthGap: PropTypes.object
};

export default WealthGapDetailsModalEnhanced;
